<script setup lang="ts">
import {
	MY_FOCUS_PATH_CODES,
	type MyFocusPathCode,
} from '#shared/area-riservata-nav';
import { useAreaRiservataPaths } from '~/composables/useAreaRiservataPaths';
import type { GoalItem, GoalPath, TabItem } from '~/components/area-riservata/my-focus/types';
import MyFocusTabNav from '~/components/area-riservata/my-focus/MyFocusTabNav.vue';
import MyFocusGoalsSection from '~/components/area-riservata/my-focus/MyFocusGoalsSection.vue';
import MyFocusListSection from '~/components/area-riservata/my-focus/MyFocusListSection.vue';
import MyFocusListItemCard from '~/components/area-riservata/my-focus/MyFocusListItemCard.vue';
import NutritionProgressChart from '~/components/area-riservata/my-focus/NutritionProgressChart.vue';
import NutritionChartParamSelect from '~/components/area-riservata/my-focus/NutritionChartParamSelect.vue';
import Input from '~/components/ui/input/Input.vue';
import type { NutritionRecordSummary } from '~/server/api/area-riservata/nutrition-records.get';
import {
	DEFAULT_NUTRITION_CHART_PARAM,
	getParamUnit,
	type NutritionChartParamKey,
} from '~/components/area-riservata/my-focus/nutrition-chart-params';

definePageMeta({
	layout: 'area-riservata',
	middleware: 'auth',
});

const { pathsList, loadPaths } = useAreaRiservataPaths();

/** Goal con path espanso (come restituito dall’API) */
type GoalItemPage = GoalItem;

const { accessToken } = useAuth();
const { authFetch } = useAuthFetch();

const goals = ref<GoalItemPage[]>([]);
const goalsLoading = ref(true);
const showPastGoals = ref(false);

/** Tab da mostrare: ordine da path.sort (pathsList già ordinato dall'API); label/icon da path o fallback */
const tabs = computed((): TabItem[] => {
	const byCode = new Map(pathsList.value.map(p => [p.code, p]));
	const fallbacks: Record<MyFocusPathCode, { label: string; shortTitle: string; icon: string }> = {
		health_path: { label: 'Percorso riabilitativo', shortTitle: 'Salute', icon: 'favorite' },
		nutrition_path: { label: 'Percorso nutrizione', shortTitle: 'Nutrizione', icon: 'restaurant' },
		fitness_path: { label: 'Percorso estetico', shortTitle: 'Fitness', icon: 'fitness_center' },
		mental_path: { label: 'Percorso psicoterapeutico', shortTitle: 'Psicoterapia', icon: 'psychology' },
	};
	const orderedFromApi = pathsList.value
		.map(p => p.code)
		.filter((code): code is MyFocusPathCode => code != null && MY_FOCUS_PATH_CODES.includes(code as MyFocusPathCode));
	const missingCodes = MY_FOCUS_PATH_CODES.filter(c => !orderedFromApi.includes(c));
	const codeOrder = [...orderedFromApi, ...missingCodes];

	const result: TabItem[] = [];
	for (const code of codeOrder) {
		const p = byCode.get(code);
		const fb = fallbacks[code];
		result.push({
			id: code,
			label: (p?.title ?? fb?.label ?? code).toString().toUpperCase(),
			shortTitle: (p?.short_title ?? fb?.shortTitle ?? p?.title ?? fb?.label ?? code).toString(),
			icon: (p?.icon ?? fb?.icon ?? 'folder').toString(),
		});
	}
	return result;
});

const PATH_DEFAULT_COLORS: Record<MyFocusPathCode, string> = {
	health_path: '#E35169',
	nutrition_path: '#2ECDA7',
	fitness_path: '#FFA439',
	mental_path: '#3399FF',
};

const activeTab = ref<MyFocusPathCode>('fitness_path');

function tabColor(tabId: MyFocusPathCode): string {
	const pathItem = pathsList.value.find(p => p.code === tabId);
	if (pathItem?.color) {
		const c = String(pathItem.color).trim();
		return c.startsWith('#') ? c : `#${c}`;
	}
	const goalWithPath = goals.value.find(g => goalPercorsoId(g) === tabId);
	if (goalWithPath) {
		const c = goalPathColor(goalWithPath);
		if (c) return c;
	}
	return PATH_DEFAULT_COLORS[tabId];
}

const goalsInProgress = computed(() =>
	goals.value.filter(g => g.completion_status !== 'completed')
);
const goalsCompleted = computed(() =>
	goals.value.filter(g => g.completion_status === 'completed')
);

function goalPercorsoId(goal: GoalItemPage): MyFocusPathCode | 'other' {
	const path = goal.path;
	if (!path || typeof path === 'number') return 'other';
	const code = (path as GoalPath).code;
	if (code && MY_FOCUS_PATH_CODES.includes(code as MyFocusPathCode)) {
		return code as MyFocusPathCode;
	}
	return 'other';
}

const goalsInProgressForActiveTab = computed(() =>
	goalsInProgress.value.filter(g => goalPercorsoId(g) === activeTab.value)
);

const goalsCompletedForActiveTab = computed(() =>
	goalsCompleted.value.filter(g => goalPercorsoId(g) === activeTab.value)
);

const hasCompletedGoalsForActiveTab = computed(
	() => goalsCompletedForActiveTab.value.length > 0
);

function goalCardTitle(goal: GoalItemPage): string {
	const t = goal.title?.trim();
	return t ? String(t).toUpperCase() : 'OBIETTIVO';
}

function goalSubtitle(goal: GoalItemPage): string {
	return goal.description?.trim() ?? '—';
}

function goalPercentage(goal: GoalItemPage): number {
	const p = goal.completion_percentage;
	if (p != null && typeof p === 'number') return Math.min(100, Math.max(0, p));
	return 0;
}

function goalPathColor(goal: GoalItemPage): string {
	const path = goal.path;
	if (!path || typeof path === 'number') return '#0d9488';
	const c = (path as GoalPath).color;
	if (c && String(c).trim()) {
		const hex = String(c).trim();
		return hex.startsWith('#') ? hex : `#${hex}`;
	}
	const defaults = ['#0d9488', '#6366f1', '#84cc16'];
	const idx = goal.id % defaults.length;
	return defaults[idx] ?? '#0d9488';
}

function formatGoalDate(iso: string | null | undefined): string {
	if (!iso) return '';
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return '';
	return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatNutritionDate(iso: string | null | undefined): string {
	if (!iso) return '—';
	try {
		const d = new Date(iso);
		return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
	} catch {
		return iso;
	}
}

async function loadGoals() {
	goalsLoading.value = true;
	if (!accessToken.value && !(process.client && localStorage.getItem('directus_access_token'))) {
		goalsLoading.value = false;
		return;
	}
	try {
		const res = await authFetch<{ goals: GoalItemPage[] }>('/api/area-riservata/goals');
		goals.value = res?.goals ?? [];
	} catch {
		goals.value = [];
	} finally {
		goalsLoading.value = false;
	}
}

// ——— Visite nutrizionali ———
const nutritionRecords = ref<NutritionRecordSummary[]>([]);
const selectedNutritionChartParam = ref<NutritionChartParamKey>(DEFAULT_NUTRITION_CHART_PARAM);
/** Filtri opzionali periodo: formato YYYY-MM-DD per input date, vuoti = tutti i dati */
const nutritionDateFrom = ref<string>('');
const nutritionDateTo = ref<string>('');
const isNutritionRecordsLoading = ref(false);
const nutritionRecordsError = ref<string | null>(null);
const nutritionRecordsPerPage = 3;
const currentNutritionRecordsPage = ref(1);

/** Visite filtrate per data inizio/fine (se impostate); altrimenti tutte */
const filteredNutritionRecords = computed(() => {
	const list = nutritionRecords.value;
	const from = nutritionDateFrom.value.trim();
	const to = nutritionDateTo.value.trim();
	if (!from && !to) return list;
	return list.filter((r) => {
		const apt = r.appointment_date;
		if (!apt) return false;
		const day = apt.slice(0, 10); // YYYY-MM-DD
		if (from && day < from) return false;
		if (to && day > to) return false;
		return true;
	});
});

const paginatedNutritionRecords = computed(() => {
	if (!filteredNutritionRecords.value.length) return [];
	const start = (currentNutritionRecordsPage.value - 1) * nutritionRecordsPerPage;
	return filteredNutritionRecords.value.slice(start, start + nutritionRecordsPerPage);
});

const totalNutritionRecordsPages = computed(() =>
	filteredNutritionRecords.value.length
		? Math.ceil(filteredNutritionRecords.value.length / nutritionRecordsPerPage)
		: 1,
);

watch([nutritionDateFrom, nutritionDateTo], () => {
	currentNutritionRecordsPage.value = 1;
});

function getRecordParamValue(r: NutritionRecordSummary, key: NutritionChartParamKey): number | null {
	const v = (r as Record<string, unknown>)[key];
	if (v == null) return null;
	const n = Number(v);
	return Number.isNaN(n) ? null : n;
}

/** Record ordinati per data con valore per il parametro selezionato (come per il grafico) */
const nutritionRecordsForParam = computed(() => {
	const list = filteredNutritionRecords.value.filter(
		(r) => r.appointment_date != null && getRecordParamValue(r, selectedNutritionChartParam.value) != null
	);
	return [...list].sort((a, b) => new Date(a.appointment_date!).getTime() - new Date(b.appointment_date!).getTime());
});

/** KPI: delta (fine - inizio) e percentuale di scostamento per il parametro nel periodo */
const nutritionPeriodKpi = computed(() => {
	const list = nutritionRecordsForParam.value;
	if (list.length < 2) return { delta: null, deltaFormatted: null, pct: null, pctFormatted: null };
	const firstVal = getRecordParamValue(list[0], selectedNutritionChartParam.value)!;
	const lastVal = getRecordParamValue(list[list.length - 1], selectedNutritionChartParam.value)!;
	const delta = lastVal - firstVal;
	const unit = getParamUnit(selectedNutritionChartParam.value);
	const deltaFormatted = unit ? `${delta >= 0 ? '+' : ''}${delta.toFixed(1)} ${unit}` : `${delta >= 0 ? '+' : ''}${delta.toFixed(1)}`;
	const pct = firstVal === 0 ? null : (delta / firstVal) * 100;
	const pctFormatted = pct != null ? `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%` : null;
	return { delta, deltaFormatted, pct, pctFormatted };
});

async function loadNutritionRecords() {
	isNutritionRecordsLoading.value = true;
	nutritionRecordsError.value = null;
	try {
		const res = await authFetch<{ nutrition_records: NutritionRecordSummary[] }>(
			'/api/area-riservata/nutrition-records',
		);
		nutritionRecords.value = Array.isArray(res.nutrition_records) ? res.nutrition_records : [];
		currentNutritionRecordsPage.value = 1;
	} catch (e: unknown) {
		const err = e as { data?: { statusMessage?: string; message?: string }; message?: string };
		nutritionRecordsError.value =
			err?.data?.statusMessage || err?.data?.message || err?.message || 'Errore nel caricamento delle visite.';
		nutritionRecords.value = [];
	} finally {
		isNutritionRecordsLoading.value = false;
	}
}

/** Dettaglio visita nutrizionale (da API nutrition-records/[id]) */
interface NutritionRecordDetail {
	id: number;
	appointment_date: string | null;
	notes: string | null;
	total_weight: number | null;
	body_fat_percentage: number | null;
	total_lean_mass: number | null;
	skeletal_muscle_mass: number | null;
	total_body_water: number | null;
	intracellular_water: number | null;
	circ_chest: number | null;
	circ_waist: number | null;
	circ_abdomen: number | null;
	circ_hips: number | null;
	circ_arm_relaxed: number | null;
	circ_arm_contracted: number | null;
	circ_forearm: number | null;
	circ_thigh: number | null;
	circ_calf: number | null;
	ratio_waist_shoulders: number | null;
	ratio_chest_waist: number | null;
	ratio_arms_calves: number | null;
	ratio_thigh_waist: number | null;
	complete_blood_count: string | null;
	lipid_profile: string | null;
	blood_glucose_insulin: string | null;
	thyroid_hormones: string | null;
	testosterone_cortisol: string | null;
	vitamin_d: string | null;
	ferritin: string | null;
}

const selectedNutritionRecordId = ref<number | null>(null);
const selectedNutritionRecordDetail = ref<NutritionRecordDetail | null>(null);
const isNutritionRecordDetailLoading = ref(false);
const nutritionRecordDetailError = ref<string | null>(null);

function formatDetailNum(value: number | null): string {
	if (value == null) return '—';
	return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

async function openNutritionRecordDetail(id: number) {
	selectedNutritionRecordId.value = id;
	selectedNutritionRecordDetail.value = null;
	nutritionRecordDetailError.value = null;
	isNutritionRecordDetailLoading.value = true;
	try {
		const res = await authFetch<{ visit: NutritionRecordDetail }>(
			`/api/area-riservata/nutrition-records/${id}`,
		);
		selectedNutritionRecordDetail.value = res?.visit ?? null;
	} catch (e: unknown) {
		const err = e as { data?: { statusMessage?: string; message?: string }; message?: string };
		nutritionRecordDetailError.value =
			err?.data?.statusMessage || err?.data?.message || err?.message || 'Errore nel caricamento del dettaglio visita.';
		selectedNutritionRecordDetail.value = null;
	} finally {
		isNutritionRecordDetailLoading.value = false;
	}
}

function closeNutritionRecordDetail() {
	selectedNutritionRecordId.value = null;
	selectedNutritionRecordDetail.value = null;
	nutritionRecordDetailError.value = null;
}

// ——— Piani alimentari ———
interface DietSummary {
	id: number;
	title: string;
	description: string | null;
	start_date: string | null;
	end_date: string | null;
	total_kcal: number | null;
	notes: string | null;
}

type DietWeekNumber = 'week-1' | 'week-2' | 'week-3' | 'week-4';
type DietWeekday = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
type DietMealType = 'breakfast' | 'morning_snack' | 'lunch' | 'afternoon_snack' | 'dinner';

interface DietDetailOptionFood {
	id: number;
	sort: number;
	food_name: string | null;
	quantity_gr: number | null;
}

interface DietDetailOption {
	id: number;
	sort: number;
	title: string | null;
	is_base: boolean;
	foods: DietDetailOptionFood[];
}

interface DietDetailCategory {
	id: number;
	sort: number;
	title: string | null;
	is_hidden: boolean;
	options: DietDetailOption[];
}

interface DietDetailPlan {
	id: number;
	sort: number;
	title: string | null;
	show_categories: boolean;
	categories: DietDetailCategory[];
}

interface DietDetailComboMeal {
	id: number;
	sort: number;
	title: string | null;
	info: string | null;
	plans: DietDetailPlan[];
}

interface DietDetailDayMealFood {
	id: number;
	sort: number;
	food_name: string | null;
	quantity_gr: number | null;
}

interface DietDetailDayMeal {
	id: number;
	sort: number;
	meal_type: DietMealType | null;
	foods: DietDetailDayMealFood[];
}

interface DietDetailDay {
	id: number;
	sort: number;
	week_number: DietWeekNumber | null;
	weekday: DietWeekday | null;
	meals: DietDetailDayMeal[];
}

interface DietDetailMacro {
	id: number;
	sort: number;
	macro_type: 'carbohydrates' | 'proteins' | 'fats' | null;
	grams_per_day: number | null;
	grams_per_kg: number | null;
	percentage: number | null;
}

interface DietDetail {
	id: number;
	title: string;
	description: string | null;
	start_date: string | null;
	end_date: string | null;
	TDEE: number | null;
	TDEE_delta: number | null;
	total_kcal: number | null;
	water: number | null;
	notes: string | null;
	macros: DietDetailMacro[];
	combo_meals: DietDetailComboMeal[];
	days: DietDetailDay[];
}

const diets = ref<DietSummary[]>([]);
const isDietsLoading = ref(false);
const dietsError = ref<string | null>(null);
const dietsPerPage = 3;
const currentDietsPage = ref(1);
const selectedDietId = ref<number | null>(null);
const selectedDietDetail = ref<DietDetail | null>(null);
const isDietDetailLoading = ref(false);
const dietDetailError = ref<string | null>(null);

const paginatedDiets = computed(() => {
	if (!diets.value.length) return [];
	const start = (currentDietsPage.value - 1) * dietsPerPage;
	return diets.value.slice(start, start + dietsPerPage);
});

const totalDietsPages = computed(() =>
	diets.value.length ? Math.ceil(diets.value.length / dietsPerPage) : 1,
);

function formatDietDate(iso: string | null | undefined): string {
	return formatNutritionDate(iso);
}

async function loadDiets() {
	isDietsLoading.value = true;
	dietsError.value = null;
	try {
		const res = await authFetch<{ diets: DietSummary[] }>('/api/area-riservata/diets');
		diets.value = Array.isArray(res.diets) ? res.diets : [];
		currentDietsPage.value = 1;
	} catch (e: unknown) {
		const err = e as { data?: { statusMessage?: string; message?: string }; message?: string };
		dietsError.value =
			err?.data?.statusMessage || err?.data?.message || err?.message || 'Errore nel caricamento dei piani alimentari.';
		diets.value = [];
	} finally {
		isDietsLoading.value = false;
	}
}

const DIET_WEEK_ORDER: DietWeekNumber[] = ['week-1', 'week-2', 'week-3', 'week-4'];
const DIET_WEEKDAY_ORDER: DietWeekday[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DIET_MEAL_TYPE_ORDER: DietMealType[] = ['breakfast', 'morning_snack', 'lunch', 'afternoon_snack', 'dinner'];

const selectedDietWeekGroups = computed(() => {
	if (!selectedDietDetail.value?.days?.length) return [] as Array<{ week: DietWeekNumber | null; days: DietDetailDay[] }>;
	const grouped = new Map<string, DietDetailDay[]>();
	for (const day of selectedDietDetail.value.days) {
		const key = day.week_number ?? 'week-1';
		if (!grouped.has(key)) grouped.set(key, []);
		grouped.get(key)!.push(day);
	}
	return Array.from(grouped.entries())
		.map(([week, days]) => ({
			week: (week as DietWeekNumber) ?? null,
			days: [...days].sort((a, b) => dietWeekdaySortIndex(a.weekday) - dietWeekdaySortIndex(b.weekday)),
		}))
		.sort((a, b) => dietWeekSortIndex(a.week) - dietWeekSortIndex(b.week));
});

function dietWeekSortIndex(week: DietWeekNumber | null): number {
	if (!week) return 999;
	const idx = DIET_WEEK_ORDER.indexOf(week);
	return idx === -1 ? 999 : idx;
}

function dietWeekdaySortIndex(day: DietWeekday | null): number {
	if (!day) return 999;
	const idx = DIET_WEEKDAY_ORDER.indexOf(day);
	return idx === -1 ? 999 : idx;
}

function dietMealTypeSortIndex(mealType: DietMealType | null): number {
	if (!mealType) return 999;
	const idx = DIET_MEAL_TYPE_ORDER.indexOf(mealType);
	return idx === -1 ? 999 : idx;
}

function dietWeekLabel(week: DietWeekNumber | null): string {
	if (!week) return 'Settimana';
	const idx = DIET_WEEK_ORDER.indexOf(week);
	return idx === -1 ? 'Settimana' : `Settimana ${idx + 1}`;
}

function dietWeekdayLabel(weekday: DietWeekday | null): string {
	if (!weekday) return 'Giorno';
	const labels: Record<DietWeekday, string> = {
		monday: 'Lunedì',
		tuesday: 'Martedì',
		wednesday: 'Mercoledì',
		thursday: 'Giovedì',
		friday: 'Venerdì',
		saturday: 'Sabato',
		sunday: 'Domenica',
	};
	return labels[weekday] ?? 'Giorno';
}

function dietMealTypeLabel(mealType: DietMealType | null): string {
	if (!mealType) return 'Pasto';
	const labels: Record<DietMealType, string> = {
		breakfast: 'Colazione',
		morning_snack: 'Spuntino mattina',
		lunch: 'Pranzo',
		afternoon_snack: 'Spuntino pomeriggio',
		dinner: 'Cena',
	};
	return labels[mealType] ?? 'Pasto';
}

function sortComboMealPlans(plans: DietDetailPlan[]): DietDetailPlan[] {
	return [...plans].sort((a, b) => a.sort - b.sort);
}

function sortPlanCategories(categories: DietDetailCategory[]): DietDetailCategory[] {
	return [...categories].sort((a, b) => a.sort - b.sort);
}

function sortCategoryOptions(options: DietDetailOption[]): DietDetailOption[] {
	return [...options].sort((a, b) => {
		if (a.is_base && !b.is_base) return -1;
		if (!a.is_base && b.is_base) return 1;
		return a.sort - b.sort;
	});
}

function getVisibleCategoryOptions(category: DietDetailCategory): DietDetailOption[] {
	return sortCategoryOptions(category.options).filter((option) => (option.foods?.length ?? 0) > 0);
}

function getVisibleCategoryNonBaseOptions(category: DietDetailCategory): DietDetailOption[] {
	return getVisibleCategoryOptions(category).filter((option) => !option.is_base);
}

function getPlanBaseOption(plan: DietDetailPlan): DietDetailOption | null {
	for (const category of sortPlanCategories(plan.categories)) {
		const base = getVisibleCategoryOptions(category).find((option) => option.is_base);
		if (base) return base;
	}
	return null;
}

function sortDayMeals(meals: DietDetailDayMeal[]): DietDetailDayMeal[] {
	return [...meals].sort((a, b) => dietMealTypeSortIndex(a.meal_type) - dietMealTypeSortIndex(b.meal_type));
}

function getVisibleDayMeals(day: DietDetailDay): DietDetailDayMeal[] {
	return sortDayMeals(day.meals).filter((meal) => (meal.foods?.length ?? 0) > 0);
}

function formatFoodQuantity(quantity: number | null): string {
	if (quantity == null) return '';
	return `${Number.isInteger(quantity) ? quantity : quantity.toFixed(1)} g`;
}

function formatDietMetric(value: number | null, suffix = ''): string {
	if (value == null) return '—';
	const out = Number.isInteger(value) ? String(value) : value.toFixed(1);
	return suffix ? `${out} ${suffix}` : out;
}

function macroTypeLabel(macroType: DietDetailMacro['macro_type']): string {
	if (macroType === 'carbohydrates') return 'Carboidrati';
	if (macroType === 'proteins') return 'Proteine';
	if (macroType === 'fats') return 'Grassi';
	return 'Macro';
}

function formatOptionFoodsInline(option: DietDetailOption): string {
	const foods = [...(option.foods ?? [])].sort((a, b) => a.sort - b.sort);
	if (!foods.length) return '—';
	return foods
		.map((food) => {
			const name = food.food_name?.trim() || 'Alimento';
			const qty = formatFoodQuantity(food.quantity_gr);
			return qty ? `${qty} ${name}` : name;
		})
		.join(' + ');
}

function formatMealFoodsInline(meal: DietDetailDayMeal): string {
	const foods = [...(meal.foods ?? [])].sort((a, b) => a.sort - b.sort);
	if (!foods.length) return '—';
	return foods
		.map((food) => {
			const name = food.food_name?.trim() || 'Alimento';
			const qty = formatFoodQuantity(food.quantity_gr);
			return qty ? `${qty} ${name}` : name;
		})
		.join(' + ');
}

async function openDietDetail(id: number) {
	selectedDietId.value = id;
	selectedDietDetail.value = null;
	dietDetailError.value = null;
	isDietDetailLoading.value = true;
	try {
		const res = await authFetch<{ diet: DietDetail }>(`/api/area-riservata/diets/${id}`);
		selectedDietDetail.value = res?.diet ?? null;
	} catch (e: unknown) {
		const err = e as { data?: { statusMessage?: string; message?: string }; message?: string };
		dietDetailError.value =
			err?.data?.statusMessage || err?.data?.message || err?.message || 'Errore nel caricamento del dettaglio piano alimentare.';
		selectedDietDetail.value = null;
	} finally {
		isDietDetailLoading.value = false;
	}
}

function closeDietDetail() {
	selectedDietId.value = null;
	selectedDietDetail.value = null;
	dietDetailError.value = null;
}

// ——— Piani di allenamento ———
interface TrainingPlanSummary {
	id: number;
	title: string;
	description: string | null;
	start_date: string | null;
	end_date: string | null;
	status: string | null;
	phase: string | null;
	microcycles_count: number;
	weeks_count: number;
}

/** Dettaglio piano (allineato a API training-plans/[id]) */
interface TrainingPlanDetailWeekSet {
	week_id: number;
	sets: number | null;
	repetitions: string | null;
}
interface TrainingPlanDetailExercise {
	id: number;
	exercise_name: string | null;
	time: string | null;
	load: string | null;
	sort: number;
	week_sets: TrainingPlanDetailWeekSet[];
	video_type: 'youtube' | 'file' | null;
	video_youtube_url: string | null;
	video_file_id: string | null;
}
interface TrainingPlanDetailBlock {
	id: number;
	block_name: string | null;
	description: string | null;
	notes: string | null;
	sort: number;
	exercises: TrainingPlanDetailExercise[];
}
interface TrainingPlanDetailWeek {
	id: number;
	name: string | null;
	sort: number;
	completed: boolean;
}
interface TrainingPlanDetailCycle {
	id: number;
	title: string | null;
	starts_on_week_number: number;
	sort: number;
	weeks: TrainingPlanDetailWeek[];
	blocks: TrainingPlanDetailBlock[];
}
interface TrainingPlanDetail {
	id: number;
	title: string | null;
	description: string | null;
	start_date: string | null;
	end_date: string | null;
	status: string | null;
	phase: string | null;
	cycles: TrainingPlanDetailCycle[];
}

const trainingPlans = ref<TrainingPlanSummary[]>([]);
const isTrainingPlansLoading = ref(false);
const trainingPlansError = ref<string | null>(null);
const trainingPlansPerPage = 3;
const currentTrainingPlansPage = ref(1);

const paginatedTrainingPlans = computed(() => {
	if (!trainingPlans.value.length) return [];
	const start = (currentTrainingPlansPage.value - 1) * trainingPlansPerPage;
	return trainingPlans.value.slice(start, start + trainingPlansPerPage);
});

const totalTrainingPlansPages = computed(() =>
	trainingPlans.value.length
		? Math.ceil(trainingPlans.value.length / trainingPlansPerPage)
		: 1,
);

function trainingPlanStatusLabel(status: string | null | undefined): string {
	if (!status) return '';
	const map: Record<string, string> = {
		draft: 'Bozza',
		'in-progress': 'In corso',
		completed: 'Completato',
	};
	return map[status] ?? status;
}

function trainingPlanPhaseLabel(phase: string | null | undefined): string {
	if (!phase) return '';
	const map: Record<string, string> = {
		rcf: 'RCF',
		sm: 'SM',
		cm: 'CM',
		cdf: 'CDF',
		rh: 'RH',
		rtp: 'RTP',
		rts: 'RTS',
	};
	return map[phase] ?? phase;
}

function getTrainingPlanStatusChipClass(status: string | null | undefined): string {
	if (!status) return 'border-gray-200 bg-gray-50 text-gray-700';
	switch (status) {
		case 'draft':
			return 'border-gray-200 bg-gray-50 text-gray-700';
		case 'in-progress':
			return 'border-amber-200 bg-amber-50 text-amber-700';
		case 'completed':
			return 'border-emerald-200 bg-emerald-50 text-emerald-700';
		default:
			return 'border-gray-200 bg-gray-50 text-gray-700';
	}
}

async function loadTrainingPlans() {
	isTrainingPlansLoading.value = true;
	trainingPlansError.value = null;
	try {
		const res = await authFetch<{ training_plans: TrainingPlanSummary[] }>(
			'/api/area-riservata/training-plans',
		);
		trainingPlans.value = Array.isArray(res.training_plans) ? res.training_plans : [];
		currentTrainingPlansPage.value = 1;
	} catch (e: unknown) {
		const err = e as { data?: { statusMessage?: string; message?: string }; message?: string };
		trainingPlansError.value =
			err?.data?.statusMessage || err?.data?.message || err?.message || 'Errore nel caricamento dei piani di allenamento.';
		trainingPlans.value = [];
	} finally {
		isTrainingPlansLoading.value = false;
	}
}

const selectedTrainingPlanId = ref<number | null>(null);
const selectedTrainingPlanDetail = ref<TrainingPlanDetail | null>(null);
const isTrainingPlanDetailLoading = ref(false);
const trainingPlanDetailError = ref<string | null>(null);
/** Accordion microcicli: chiave "cycleId-weekId" se aperto */
const trainingPlanCycleWeekOpen = ref<Record<string, boolean>>({});

function toggleTrainingPlanCycleWeek(cycleId: number, weekId: number) {
	const key = `${cycleId}-${weekId}`;
	trainingPlanCycleWeekOpen.value[key] = !trainingPlanCycleWeekOpen.value[key];
}

function isTrainingPlanCycleWeekOpen(cycleId: number, weekId: number): boolean {
	return !!trainingPlanCycleWeekOpen.value[`${cycleId}-${weekId}`];
}

/** Restituisce set/ripetizioni per un esercizio in una settimana */
function getExerciseWeekSet(
	exercise: TrainingPlanDetailExercise,
	weekId: number,
): { sets: number | null; repetitions: string | null } {
	const ws = exercise.week_sets?.find((s) => s.week_id === weekId);
	return ws ? { sets: ws.sets ?? null, repetitions: ws.repetitions ?? null } : { sets: null, repetitions: null };
}

/** Imposta l’accordion: chiuse tutte le settimane, aperta solo la prima non completata di ogni microciclo */
function setTrainingPlanDefaultOpenState(plan: TrainingPlanDetail | null) {
	const next: Record<string, boolean> = {};
	if (!plan?.cycles) return next;
	for (const cycle of plan.cycles) {
		const sortedWeeks = (cycle.weeks ?? []).slice().sort((a, b) => a.sort - b.sort);
		const firstIncomplete = sortedWeeks.find((w) => !w.completed);
		if (firstIncomplete) {
			next[`${cycle.id}-${firstIncomplete.id}`] = true;
		}
	}
	trainingPlanCycleWeekOpen.value = next;
}

async function openTrainingPlanDetail(id: number) {
	selectedTrainingPlanId.value = id;
	selectedTrainingPlanDetail.value = null;
	trainingPlanDetailError.value = null;
	trainingPlanCycleWeekOpen.value = {};
	isTrainingPlanDetailLoading.value = true;
	try {
		const res = await authFetch<{ plan: TrainingPlanDetail }>(
			`/api/area-riservata/training-plans/${id}`,
		);
		const plan = res?.plan ?? null;
		selectedTrainingPlanDetail.value = plan;
		setTrainingPlanDefaultOpenState(plan);
	} catch (e: unknown) {
		const err = e as { data?: { statusMessage?: string; message?: string }; message?: string };
		trainingPlanDetailError.value =
			err?.data?.statusMessage || err?.data?.message || err?.message || 'Errore nel caricamento del dettaglio piano.';
		selectedTrainingPlanDetail.value = null;
	} finally {
		isTrainingPlanDetailLoading.value = false;
	}
}

function closeTrainingPlanDetail() {
	selectedTrainingPlanId.value = null;
	selectedTrainingPlanDetail.value = null;
	trainingPlanDetailError.value = null;
	trainingPlanCycleWeekOpen.value = {};
	exerciseVideoModal.value = null;
}

/** Modale video esercizio: { exerciseName, video_type, video_youtube_url, video_file_id } */
const exerciseVideoModal = ref<{
	exerciseName: string;
	video_type: 'youtube' | 'file';
	video_youtube_url: string | null;
	video_file_id: string | null;
} | null>(null);

function hasExerciseVideo(ex: TrainingPlanDetailExercise): boolean {
	return (ex.video_type === 'youtube' && !!ex.video_youtube_url?.trim()) ||
		(ex.video_type === 'file' && !!ex.video_file_id?.trim());
}

/** Estrae l’ID video (11 caratteri) da URL YouTube. */
function youtubeVideoId(url: string | null): string | null {
	if (!url?.trim()) return null;
	const u = url.trim();
	const idMatch = u.match(/(?:v=|\/embed\/|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:[?&]|$)/) ||
		u.match(/(?:v=|\/embed\/|youtu\.be\/)([a-zA-Z0-9_-]+)/);
	return idMatch ? idMatch[1].slice(0, 11) : null;
}

/** URL embed YouTube (con referrerPolicy sulla pagina/iframe per ridurre errore 153). */
function youtubeEmbedUrl(url: string | null): string | null {
	const id = youtubeVideoId(url);
	return id ? `https://www.youtube-nocookie.com/embed/${id}?rel=0` : null;
}

/** URL pagina watch su YouTube (fallback / apri in nuova scheda). */
function youtubeWatchUrl(url: string | null): string | null {
	const id = youtubeVideoId(url);
	return id ? `https://www.youtube.com/watch?v=${id}` : null;
}

/** URL thumbnail YouTube (fallback se embed non disponibile). */
function youtubeThumbnailUrl(url: string | null): string | null {
	const id = youtubeVideoId(url);
	return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

function exerciseVideoFileUrl(fileId: string | null): string | null {
	if (!fileId?.trim() || !directusUrl) return null;
	const token = accessToken.value || (process.client ? localStorage.getItem('directus_access_token') : null);
	const base = `${directusUrl}/assets/${fileId.trim()}`;
	return token ? `${base}?access_token=${token}` : base;
}

function openExerciseVideoModal(ex: TrainingPlanDetailExercise) {
	if (!hasExerciseVideo(ex)) return;
	exerciseVideoModal.value = {
		exerciseName: ex.exercise_name ?? 'Esercizio',
		video_type: ex.video_type!,
		video_youtube_url: ex.video_youtube_url ?? null,
		video_file_id: ex.video_file_id ?? null,
	};
}

function closeExerciseVideoModal() {
	exerciseVideoModal.value = null;
}

// ——— Valutazioni sportive ———
interface SportRecordSummary {
	id: number;
	notes: string | null;
	appointment_date: string | null;
}

const sportRecords = ref<SportRecordSummary[]>([]);
const isSportRecordsLoading = ref(false);
const sportRecordsError = ref<string | null>(null);
const sportRecordsPerPage = 3;
const currentSportRecordsPage = ref(1);

const paginatedSportRecords = computed(() => {
	if (!sportRecords.value.length) return [];
	const start = (currentSportRecordsPage.value - 1) * sportRecordsPerPage;
	return sportRecords.value.slice(start, start + sportRecordsPerPage);
});

const totalSportRecordsPages = computed(() =>
	sportRecords.value.length
		? Math.ceil(sportRecords.value.length / sportRecordsPerPage)
		: 1,
);

async function loadSportRecords() {
	isSportRecordsLoading.value = true;
	sportRecordsError.value = null;
	try {
		const res = await authFetch<{ sport_records: SportRecordSummary[] }>(
			'/api/area-riservata/sport-records',
		);
		sportRecords.value = Array.isArray(res.sport_records) ? res.sport_records : [];
		currentSportRecordsPage.value = 1;
	} catch (e: unknown) {
		const err = e as { data?: { statusMessage?: string; message?: string }; message?: string };
		sportRecordsError.value =
			err?.data?.statusMessage || err?.data?.message || err?.message || 'Errore nel caricamento delle valutazioni sportive.';
		sportRecords.value = [];
	} finally {
		isSportRecordsLoading.value = false;
	}
}

/** Dettaglio valutazione sportiva (da API sport-records/[id]) */
interface SportRecordExerciseDetail {
	id: number;
	exercise_name: string | null;
	is_isometric: boolean;
	is_MVC: boolean;
	show_notes: boolean;
	show_percentage: boolean;
	load: string | null;
	right_evaluation: number | null;
	left_evaluation: number | null;
	isometric_evaluation: number | null;
	mvc_20: number | null;
	mvc_40: number | null;
	mvc_60: number | null;
	notes: string | null;
	percentage: number | null;
	sort: number;
}
interface SportRecordSectionDetail {
	id: number;
	section_title: string | null;
	sort: number;
	exercises: SportRecordExerciseDetail[];
}
interface SportRecordCategoryDetail {
	id: number;
	evaluation_type_title: string | null;
	sort: number;
	sections: SportRecordSectionDetail[];
}
interface SportRecordDetail {
	id: number;
	appointment_date: string | null;
	notes: string | null;
	evaluation_types: SportRecordCategoryDetail[];
}

const selectedSportRecordId = ref<number | null>(null);
const selectedSportRecordDetail = ref<SportRecordDetail | null>(null);
const isSportRecordDetailLoading = ref(false);
const sportRecordDetailError = ref<string | null>(null);

async function openSportRecordDetail(id: number) {
	selectedSportRecordId.value = id;
	selectedSportRecordDetail.value = null;
	sportRecordDetailError.value = null;
	isSportRecordDetailLoading.value = true;
	try {
		const res = await authFetch<{ visit: SportRecordDetail }>(
			`/api/area-riservata/sport-records/${id}`,
		);
		selectedSportRecordDetail.value = res?.visit ?? null;
	} catch (e: unknown) {
		const err = e as { data?: { statusMessage?: string; message?: string }; message?: string };
		sportRecordDetailError.value =
			err?.data?.statusMessage || err?.data?.message || err?.message || 'Errore nel caricamento del dettaglio valutazione.';
		selectedSportRecordDetail.value = null;
	} finally {
		isSportRecordDetailLoading.value = false;
	}
}

function closeSportRecordDetail() {
	selectedSportRecordId.value = null;
	selectedSportRecordDetail.value = null;
	sportRecordDetailError.value = null;
}

/** Valori esercizio da mostrare in base al tipo (isometric / left+right / mvc) */
function sportExerciseDisplayValues(ex: SportRecordExerciseDetail): string {
	const parts: string[] = [];
	if (ex.load) parts.push(`Carico: ${ex.load}`);
	if (ex.is_isometric && ex.isometric_evaluation != null) {
		parts.push(`Isom.: ${formatDetailNum(ex.isometric_evaluation)}`);
	} else if (ex.is_MVC) {
		const mvc = [ex.mvc_20, ex.mvc_40, ex.mvc_60].filter((v) => v != null);
		if (mvc.length) parts.push(`MVC 20/40/60: ${mvc.map((v) => formatDetailNum(v)).join(' / ')}`);
	} else {
		if (ex.left_evaluation != null) parts.push(`Sx: ${formatDetailNum(ex.left_evaluation)}`);
		if (ex.right_evaluation != null) parts.push(`Dx: ${formatDetailNum(ex.right_evaluation)}`);
	}
	if (ex.show_percentage && ex.percentage != null) parts.push(`${formatDetailNum(ex.percentage)}%`);
	if (ex.show_notes && ex.notes) parts.push(ex.notes);
	return parts.join(' · ') || '—';
}

// ——— Sedute psicoterapeutiche ———
interface MentalRecordSummary {
	id: number;
	notes: string | null;
	sentiment: string | null;
	appointment_date: string | null;
}

const mentalRecords = ref<MentalRecordSummary[]>([]);
const isMentalRecordsLoading = ref(false);
const mentalRecordsError = ref<string | null>(null);
const mentalRecordsPerPage = 3;
const currentMentalRecordsPage = ref(1);

const paginatedMentalRecords = computed(() => {
	if (!mentalRecords.value.length) return [];
	const start = (currentMentalRecordsPage.value - 1) * mentalRecordsPerPage;
	return mentalRecords.value.slice(start, start + mentalRecordsPerPage);
});

const totalMentalRecordsPages = computed(() =>
	mentalRecords.value.length
		? Math.ceil(mentalRecords.value.length / mentalRecordsPerPage)
		: 1,
);

function mentalSentimentLabel(sentiment: string | null | undefined): string {
	if (!sentiment) return '';
	const map: Record<string, string> = {
		frustrated: 'Frustrato',
		dissatisfied: 'Insoddisfatto',
		neutral: 'Neutrale',
		satisfied: 'Soddisfatto',
		excited: 'Entusiasta',
	};
	return map[sentiment] ?? sentiment;
}

/** Icona Material per stato emotivo (come in Directus) */
function mentalSentimentIcon(sentiment: string | null | undefined): string {
	if (!sentiment) return 'sentiment_neutral';
	const map: Record<string, string> = {
		frustrated: 'sentiment_frustrated',
		dissatisfied: 'sentiment_dissatisfied',
		neutral: 'sentiment_neutral',
		satisfied: 'sentiment_satisfied',
		excited: 'sentiment_excited',
	};
	return map[sentiment] ?? 'sentiment_neutral';
}

/** Dettaglio seduta (da API mental-records/[id]) */
interface MentalRecordDetail {
	id: number;
	appointment_date: string | null;
	sentiment: string | null;
	notes: string | null;
}

const selectedMentalRecordId = ref<number | null>(null);
const selectedMentalRecordDetail = ref<MentalRecordDetail | null>(null);
const isMentalRecordDetailLoading = ref(false);
const mentalRecordDetailError = ref<string | null>(null);

async function openMentalRecordDetail(id: number) {
	selectedMentalRecordId.value = id;
	selectedMentalRecordDetail.value = null;
	mentalRecordDetailError.value = null;
	isMentalRecordDetailLoading.value = true;
	try {
		const res = await authFetch<{ visit: MentalRecordDetail }>(
			`/api/area-riservata/mental-records/${id}`,
		);
		selectedMentalRecordDetail.value = res?.visit ?? null;
	} catch (e: unknown) {
		const err = e as { data?: { statusMessage?: string; message?: string }; message?: string };
		mentalRecordDetailError.value =
			err?.data?.statusMessage || err?.data?.message || err?.message || 'Errore nel caricamento del dettaglio seduta.';
		selectedMentalRecordDetail.value = null;
	} finally {
		isMentalRecordDetailLoading.value = false;
	}
}

function closeMentalRecordDetail() {
	selectedMentalRecordId.value = null;
	selectedMentalRecordDetail.value = null;
	mentalRecordDetailError.value = null;
}

async function loadMentalRecords() {
	isMentalRecordsLoading.value = true;
	mentalRecordsError.value = null;
	try {
		const res = await authFetch<{ mental_records: MentalRecordSummary[] }>(
			'/api/area-riservata/mental-records',
		);
		mentalRecords.value = Array.isArray(res.mental_records) ? res.mental_records : [];
		currentMentalRecordsPage.value = 1;
	} catch (e: unknown) {
		const err = e as { data?: { statusMessage?: string; message?: string }; message?: string };
		mentalRecordsError.value =
			err?.data?.statusMessage || err?.data?.message || err?.message || 'Errore nel caricamento delle sedute psicoterapeutiche.';
		mentalRecords.value = [];
	} finally {
		isMentalRecordsLoading.value = false;
	}
}

// ——— Report salute (visite mediche) ———
interface HealthRecordSummary {
	id: number;
	visit_reason: string | null;
	notes: string | null;
	appointment_date: string | null;
}

/** Dettaglio visita (da API health-records/[id]) */
interface HealthRecordReportFile {
	id: string;
	filename_download: string | null;
}
interface HealthRecordDetail {
	id: number;
	visit_reason: string | null;
	notes: string | null;
	clinical_picture: string | null;
	vas: string | null;
	present_diagnostics: string | null;
	red_flags: string | null;
	medical_history_pharmacology: string | null;
	appointment_date: string | null;
	report_files: HealthRecordReportFile[];
}

const healthRecords = ref<HealthRecordSummary[]>([]);
const isHealthRecordsLoading = ref(false);
const healthRecordsError = ref<string | null>(null);
const healthRecordsPerPage = 3;
const currentHealthRecordsPage = ref(1);

const selectedHealthRecordId = ref<number | null>(null);
const selectedHealthRecordDetail = ref<HealthRecordDetail | null>(null);
const isHealthRecordDetailLoading = ref(false);
const healthRecordDetailError = ref<string | null>(null);

const config = useRuntimeConfig();
const directusUrl = config.public.directusUrl as string;

function healthReportFileDownloadUrl(fileId: string): string | null {
	if (!fileId || !directusUrl) return null;
	return `${directusUrl}/assets/${fileId}?download`;
}

async function openHealthRecordDetail(id: number) {
	selectedHealthRecordId.value = id;
	selectedHealthRecordDetail.value = null;
	healthRecordDetailError.value = null;
	isHealthRecordDetailLoading.value = true;
	try {
		const res = await authFetch<{ visit: HealthRecordDetail }>(
			`/api/area-riservata/health-records/${id}`,
		);
		selectedHealthRecordDetail.value = res?.visit ?? null;
	} catch (e: unknown) {
		const err = e as { data?: { statusMessage?: string; message?: string }; message?: string };
		healthRecordDetailError.value =
			err?.data?.statusMessage || err?.data?.message || err?.message || 'Errore nel caricamento del dettaglio visita.';
		selectedHealthRecordDetail.value = null;
	} finally {
		isHealthRecordDetailLoading.value = false;
	}
}

function closeHealthRecordDetail() {
	selectedHealthRecordId.value = null;
	selectedHealthRecordDetail.value = null;
	healthRecordDetailError.value = null;
}

const paginatedHealthRecords = computed(() => {
	if (!healthRecords.value.length) return [];
	const start = (currentHealthRecordsPage.value - 1) * healthRecordsPerPage;
	return healthRecords.value.slice(start, start + healthRecordsPerPage);
});

const totalHealthRecordsPages = computed(() =>
	healthRecords.value.length
		? Math.ceil(healthRecords.value.length / healthRecordsPerPage)
		: 1,
);

async function loadHealthRecords() {
	isHealthRecordsLoading.value = true;
	healthRecordsError.value = null;
	try {
		const res = await authFetch<{ health_records: HealthRecordSummary[] }>(
			'/api/area-riservata/health-records',
		);
		healthRecords.value = Array.isArray(res.health_records) ? res.health_records : [];
		currentHealthRecordsPage.value = 1;
	} catch (e: unknown) {
		const err = e as { data?: { statusMessage?: string; message?: string }; message?: string };
		healthRecordsError.value =
			err?.data?.statusMessage || err?.data?.message || err?.message || 'Errore nel caricamento delle visite mediche.';
		healthRecords.value = [];
	} finally {
		isHealthRecordsLoading.value = false;
	}
}

// ——— Referti medici ———
interface MedicalReportSummary {
	id: number;
	date: string | null;
	description: string | null;
	notes: string | null;
}

/** Dettaglio referto (da API medical-reports/[id]) */
interface MedicalReportDetailFile {
	id: string;
	filename_download: string | null;
}
interface MedicalReportDetail {
	id: number;
	date: string | null;
	description: string | null;
	notes: string | null;
	report_files: MedicalReportDetailFile[];
}

const medicalReports = ref<MedicalReportSummary[]>([]);
const isMedicalReportsLoading = ref(false);
const medicalReportsError = ref<string | null>(null);
const medicalReportsPerPage = 3;
const currentMedicalReportsPage = ref(1);

const selectedMedicalReportId = ref<number | null>(null);
const selectedMedicalReportDetail = ref<MedicalReportDetail | null>(null);
const isMedicalReportDetailLoading = ref(false);
const medicalReportDetailError = ref<string | null>(null);

// Creazione nuovo referto medico
interface CreateMedicalReportUploadItem {
	id: string;
	file: File;
	previewUrl: string | null;
}

const isCreateMedicalReportOpen = ref(false);
const createMedicalReportDate = ref<string>('');
const createMedicalReportDescription = ref<string>('');
const createMedicalReportNotes = ref<string>('');
const createMedicalReportFiles = ref<CreateMedicalReportUploadItem[]>([]);
const createMedicalReportDateError = ref<string | null>(null);
const createMedicalReportDescriptionError = ref<string | null>(null);
const createMedicalReportFilesError = ref<string | null>(null);
const isCreateMedicalReportSubmitting = ref(false);
const createMedicalReportError = ref<string | null>(null);

const createMedicalReportDateInputClasses = computed(() =>
	[
		'mt-1 focus:outline-none focus-visible:outline-none',
		createMedicalReportDateError.value
			? 'border-red-500 focus:border-red-500 focus-visible:border-red-500 focus:ring-red-500 focus-visible:ring-red-500'
			: 'focus:border-[#A21B1F] focus-visible:border-[#A21B1F] focus:ring-[#A21B1F] focus-visible:ring-[#A21B1F]',
	].join(' '),
);

function appendMedicalReportFiles(nextFiles: File[]) {
	if (!nextFiles.length) return;

	const mapped = nextFiles.map((file) => ({
		id: (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`),
		file,
		previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
	}));

	createMedicalReportFiles.value = [...createMedicalReportFiles.value, ...mapped];
	if (createMedicalReportFiles.value.length > 0) {
		createMedicalReportFilesError.value = null;
	}
}

function removeCreateMedicalReportFile(uploadId: string) {
	const idx = createMedicalReportFiles.value.findIndex((item) => item.id === uploadId);
	if (idx < 0) return;
	const item = createMedicalReportFiles.value[idx];
	if (item?.previewUrl) {
		URL.revokeObjectURL(item.previewUrl);
	}
	createMedicalReportFiles.value.splice(idx, 1);
}

function clearCreateMedicalReportFiles() {
	for (const item of createMedicalReportFiles.value) {
		if (item.previewUrl) {
			URL.revokeObjectURL(item.previewUrl);
		}
	}
	createMedicalReportFiles.value = [];
}

function onCreateMedicalReportFilesChange(event: Event) {
	const input = event.target as HTMLInputElement | null;
	const files = input?.files;
	appendMedicalReportFiles(files ? Array.from(files) : []);
	if (input) input.value = '';
}

function onCreateMedicalReportCameraChange(event: Event) {
	const input = event.target as HTMLInputElement | null;
	const files = input?.files;
	appendMedicalReportFiles(files ? Array.from(files) : []);
	if (input) input.value = '';
}

async function submitCreateMedicalReport() {
	createMedicalReportError.value = null;
	createMedicalReportDateError.value = null;
	createMedicalReportDescriptionError.value = null;
	createMedicalReportFilesError.value = null;

	const hasDate = !!createMedicalReportDate.value;
	const hasDescription = !!createMedicalReportDescription.value.trim();
	const hasFiles = createMedicalReportFiles.value.length > 0;

	if (!hasDate) {
		createMedicalReportDateError.value = 'Inserisci la data del referto.';
	}
	if (!hasDescription) {
		createMedicalReportDescriptionError.value = 'Inserisci una descrizione del referto.';
	}
	if (!hasFiles) {
		createMedicalReportFilesError.value = 'Carica almeno un file allegato.';
	}

	if (!hasDate || !hasDescription || !hasFiles) {
		createMedicalReportError.value = 'Controlla i campi evidenziati in rosso.';
		return;
	}

	isCreateMedicalReportSubmitting.value = true;
	try {
		const formData = new FormData();
		formData.append('date', createMedicalReportDate.value);
		formData.append('description', createMedicalReportDescription.value.trim());
		if (createMedicalReportNotes.value.trim()) {
			formData.append('notes', createMedicalReportNotes.value.trim());
		}
		for (const upload of createMedicalReportFiles.value) {
			formData.append('files', upload.file);
		}

		await authFetch('/api/area-riservata/medical-reports', {
			method: 'POST',
			body: formData,
		});

		isCreateMedicalReportOpen.value = false;
		createMedicalReportDate.value = '';
		createMedicalReportDescription.value = '';
		createMedicalReportNotes.value = '';
		clearCreateMedicalReportFiles();
		createMedicalReportDateError.value = null;
		createMedicalReportDescriptionError.value = null;
		createMedicalReportFilesError.value = null;

		await loadMedicalReports();
	} catch (e: unknown) {
		const err = e as { data?: { statusMessage?: string; message?: string }; message?: string };
		createMedicalReportError.value =
			err?.data?.statusMessage || err?.data?.message || err?.message || 'Errore durante il salvataggio del referto.';
	} finally {
		isCreateMedicalReportSubmitting.value = false;
	}
}

onBeforeUnmount(() => {
	clearCreateMedicalReportFiles();
});

async function openMedicalReportDetail(id: number) {
	selectedMedicalReportId.value = id;
	selectedMedicalReportDetail.value = null;
	medicalReportDetailError.value = null;
	isMedicalReportDetailLoading.value = true;
	try {
		const res = await authFetch<{ report: MedicalReportDetail }>(
			`/api/area-riservata/medical-reports/${id}`,
		);
		selectedMedicalReportDetail.value = res?.report ?? null;
	} catch (e: unknown) {
		const err = e as { data?: { statusMessage?: string; message?: string }; message?: string };
		medicalReportDetailError.value =
			err?.data?.statusMessage || err?.data?.message || err?.message || 'Errore nel caricamento del dettaglio referto.';
		selectedMedicalReportDetail.value = null;
	} finally {
		isMedicalReportDetailLoading.value = false;
	}
}

function closeMedicalReportDetail() {
	selectedMedicalReportId.value = null;
	selectedMedicalReportDetail.value = null;
	medicalReportDetailError.value = null;
}

const paginatedMedicalReports = computed(() => {
	if (!medicalReports.value.length) return [];
	const start = (currentMedicalReportsPage.value - 1) * medicalReportsPerPage;
	return medicalReports.value.slice(start, start + medicalReportsPerPage);
});

const totalMedicalReportsPages = computed(() =>
	medicalReports.value.length
		? Math.ceil(medicalReports.value.length / medicalReportsPerPage)
		: 1,
);

async function loadMedicalReports() {
	isMedicalReportsLoading.value = true;
	medicalReportsError.value = null;
	try {
		const res = await authFetch<{ medical_reports: MedicalReportSummary[] }>(
			'/api/area-riservata/medical-reports',
		);
		medicalReports.value = Array.isArray(res.medical_reports) ? res.medical_reports : [];
		currentMedicalReportsPage.value = 1;
	} catch (e: unknown) {
		const err = e as { data?: { statusMessage?: string; message?: string }; message?: string };
		medicalReportsError.value =
			err?.data?.statusMessage || err?.data?.message || err?.message || 'Errore nel caricamento dei referti medici.';
		medicalReports.value = [];
	} finally {
		isMedicalReportsLoading.value = false;
	}
}

// Colori accent per percorso (per le card liste)
const ACCENT_FITNESS = '#FFA439';
const ACCENT_HEALTH = '#E35169';
const ACCENT_NUTRITION = '#2ECDA7';
const ACCENT_MENTAL = '#3399FF';

onMounted(() => {
	loadPaths();
	loadGoals();
	loadNutritionRecords();
	loadDiets();
	loadTrainingPlans();
	loadSportRecords();
	loadMentalRecords();
	loadHealthRecords();
	loadMedicalReports();
});
</script>

<template>
	<div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
		<h1 class="text-2xl font-bold text-gray-900 mb-6">My Focus</h1>

		<MyFocusTabNav
			:tabs="tabs"
			:active-tab="activeTab"
			:tab-color="tabColor"
			@update:active-tab="activeTab = $event"
		/>

		<MyFocusGoalsSection
			:loading="goalsLoading"
			:goals-in-progress="goalsInProgressForActiveTab"
			:goals-completed="goalsCompletedForActiveTab"
			:has-completed="hasCompletedGoalsForActiveTab"
			:show-past-goals="showPastGoals"
			:get-title="goalCardTitle"
			:get-subtitle="goalSubtitle"
			:get-percentage="goalPercentage"
			:get-accent-color="goalPathColor"
			:format-date="formatGoalDate"
			@toggle-past-goals="showPastGoals = !showPastGoals"
		/>

		<!-- Tab Fitness -->
		<div v-if="activeTab === 'fitness_path'" class="space-y-6">
			<h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide">
				PIANI DI ALLENAMENTO
			</h2>
			<MyFocusListSection
				:loading="isTrainingPlansLoading"
				:error="trainingPlansError"
				:empty="!trainingPlans.length"
				empty-message="Nessun piano di allenamento disponibile."
				:current-page="currentTrainingPlansPage"
				:total-pages="totalTrainingPlansPages"
				@prev-page="currentTrainingPlansPage = Math.max(1, currentTrainingPlansPage - 1)"
				@next-page="currentTrainingPlansPage = Math.min(totalTrainingPlansPages, currentTrainingPlansPage + 1)"
			>
				<MyFocusListItemCard
					v-for="plan in paginatedTrainingPlans"
					:key="plan.id"
					:accent-color="ACCENT_FITNESS"
					class="cursor-pointer"
					@click="openTrainingPlanDetail(plan.id)"
				>
					<div class="flex items-start justify-between gap-3">
						<p class="text-sm font-semibold text-gray-900 min-w-0">
							{{ plan.title }}
						</p>
						<div class="flex flex-col items-end shrink-0 gap-0.5">
							<span
								v-if="trainingPlanStatusLabel(plan.status)"
								class="inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium"
								:class="getTrainingPlanStatusChipClass(plan.status)"
							>
								{{ trainingPlanStatusLabel(plan.status) }}
							</span>
						</div>
					</div>
					<div class="mt-1 flex items-center justify-between gap-3 text-xs text-gray-600">
						<span class="flex items-center gap-x-3">
							<span v-if="plan.microcycles_count != null && plan.microcycles_count > 0">
								Microcicli: {{ plan.microcycles_count }}
							</span>
							<span v-if="plan.weeks_count != null && plan.weeks_count > 0">
								Settimane: {{ plan.weeks_count }}
							</span>
						</span>
						<span
							v-if="trainingPlanPhaseLabel(plan.phase)"
							class="shrink-0"
						>
							Fase: {{ trainingPlanPhaseLabel(plan.phase) }}
						</span>
					</div>
					<div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
						<span v-if="plan.start_date && plan.end_date">
							Dal {{ formatDietDate(plan.start_date) }} al {{ formatDietDate(plan.end_date) }}
						</span>
						<span v-else-if="plan.start_date">
							Da {{ formatDietDate(plan.start_date) }}
						</span>
						<span v-else-if="plan.end_date">
							Fino al {{ formatDietDate(plan.end_date) }}
						</span>
					</div>
					<p
						v-if="plan.description"
						class="mt-1 text-sm text-gray-700 line-clamp-2"
					>
						{{ plan.description }}
					</p>
				</MyFocusListItemCard>
			</MyFocusListSection>

			<h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide">
				VALUTAZIONI SPORTIVE
			</h2>
			<MyFocusListSection
				:loading="isSportRecordsLoading"
				:error="sportRecordsError"
				:empty="!sportRecords.length"
				empty-message="Nessuna valutazione sportiva disponibile."
				:current-page="currentSportRecordsPage"
				:total-pages="totalSportRecordsPages"
				@prev-page="currentSportRecordsPage = Math.max(1, currentSportRecordsPage - 1)"
				@next-page="currentSportRecordsPage = Math.min(totalSportRecordsPages, currentSportRecordsPage + 1)"
			>
				<MyFocusListItemCard
					v-for="rec in paginatedSportRecords"
					:key="rec.id"
					:accent-color="ACCENT_FITNESS"
					class="cursor-pointer"
					@click="openSportRecordDetail(rec.id)"
				>
					<p class="text-sm font-semibold text-gray-900">
						<template v-if="rec.appointment_date">
							Valutazione del {{ formatNutritionDate(rec.appointment_date) }}
						</template>
						<template v-else>
							Valutazione
						</template>
					</p>
					<p
						v-if="rec.notes"
						class="mt-1 text-sm text-gray-700 line-clamp-2"
					>
						{{ rec.notes }}
					</p>
				</MyFocusListItemCard>
			</MyFocusListSection>
		</div>

		<!-- Tab Salute -->
		<div v-else-if="activeTab === 'health_path'" class="space-y-6">
			<h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide">
				VISITE MEDICHE
			</h2>
			<MyFocusListSection
				:loading="isHealthRecordsLoading"
				:error="healthRecordsError"
				:empty="!healthRecords.length"
				empty-message="Nessuna visita medica disponibile."
				:current-page="currentHealthRecordsPage"
				:total-pages="totalHealthRecordsPages"
				@prev-page="currentHealthRecordsPage = Math.max(1, currentHealthRecordsPage - 1)"
				@next-page="currentHealthRecordsPage = Math.min(totalHealthRecordsPages, currentHealthRecordsPage + 1)"
			>
				<MyFocusListItemCard
					v-for="rec in paginatedHealthRecords"
					:key="rec.id"
					:accent-color="ACCENT_HEALTH"
					class="cursor-pointer"
					@click="openHealthRecordDetail(rec.id)"
				>
					<p class="text-sm font-semibold text-gray-900">
						<template v-if="rec.appointment_date">
							Visita del {{ formatNutritionDate(rec.appointment_date) }}
						</template>
						<template v-else>
							Visita medica
						</template>
					</p>
					<div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
						<span v-if="rec.visit_reason">
							Motivo: {{ rec.visit_reason }}
						</span>
					</div>
					<p
						v-if="rec.notes"
						class="mt-1 text-sm text-gray-700 line-clamp-2"
					>
						{{ rec.notes }}
					</p>
				</MyFocusListItemCard>
			</MyFocusListSection>

			<div class="flex items-center justify-between gap-3">
				<h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide">
					REFERTI MEDICI
				</h2>
				<button
					type="button"
					class="inline-flex items-center justify-center gap-2 h-9 px-4 py-2 text-xs sm:text-sm font-medium font-button text-white bg-black hover:bg-black/90 rounded-none transition-colors uppercase disabled:opacity-50 disabled:cursor-not-allowed"
					@click="isCreateMedicalReportOpen = true"
				>
					<span class="material-symbols-outlined text-base sm:text-lg" aria-hidden="true">upload_file</span>
					CARICA REFERTO
				</button>
			</div>
			<MyFocusListSection
				:loading="isMedicalReportsLoading"
				:error="medicalReportsError"
				:empty="!medicalReports.length"
				empty-message="Nessun referto medico disponibile."
				:current-page="currentMedicalReportsPage"
				:total-pages="totalMedicalReportsPages"
				@prev-page="currentMedicalReportsPage = Math.max(1, currentMedicalReportsPage - 1)"
				@next-page="currentMedicalReportsPage = Math.min(totalMedicalReportsPages, currentMedicalReportsPage + 1)"
			>
				<MyFocusListItemCard
					v-for="rep in paginatedMedicalReports"
					:key="rep.id"
					:accent-color="ACCENT_HEALTH"
					class="cursor-pointer"
					@click="openMedicalReportDetail(rep.id)"
				>
					<p class="text-sm font-semibold text-gray-900">
						<template v-if="rep.date">
							Referto del {{ formatNutritionDate(rep.date) }}
						</template>
						<template v-else>
							Referto medico
						</template>
					</p>
					<p
						v-if="rep.description"
						class="mt-1 text-sm text-gray-700 line-clamp-2"
					>
						{{ rep.description }}
					</p>
					<p
						v-else-if="rep.notes"
						class="mt-1 text-sm text-gray-700 line-clamp-2"
					>
						{{ rep.notes }}
					</p>
				</MyFocusListItemCard>
			</MyFocusListSection>
		</div>

		<!-- Tab Nutrizione -->
		<div v-else-if="activeTab === 'nutrition_path'" class="space-y-6">
			<h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide">
				VISITE NUTRIZIONALI
			</h2>
			<div class="flex flex-wrap items-end gap-6 gap-y-3">
				<div class="flex flex-col gap-1">
					<span class="text-sm text-gray-600">Parametro</span>
					<NutritionChartParamSelect v-model="selectedNutritionChartParam" />
				</div>
				<div class="flex flex-col gap-1">
					<label for="nutrition-date-from" class="text-sm text-gray-600">Da</label>
					<input
						id="nutrition-date-from"
						v-model="nutritionDateFrom"
						type="date"
						class="rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					/>
				</div>
				<div class="flex flex-col gap-1">
					<label for="nutrition-date-to" class="text-sm text-gray-600">A</label>
					<input
						id="nutrition-date-to"
						v-model="nutritionDateTo"
						type="date"
						class="rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					/>
				</div>
			</div>
			<div v-if="filteredNutritionRecords.length > 0" class="flex flex-col gap-4 sm:flex-row sm:items-stretch">
				<div
					v-if="nutritionRecordsForParam.length >= 2"
					class="flex shrink-0 flex-col gap-3 sm:w-40"
				>
					<div
						class="rounded-none border border-gray-200 bg-white px-4 py-3 shadow-sm"
						:style="{ borderLeftColor: tabColor('nutrition_path'), borderLeftWidth: '4px' }"
					>
						<p class="text-xs font-medium uppercase tracking-wide text-gray-500">
							Delta periodo
						</p>
						<p class="mt-1 text-lg font-semibold text-gray-900">
							{{ nutritionPeriodKpi.deltaFormatted ?? '—' }}
						</p>
					</div>
					<div
						class="rounded-none border border-gray-200 bg-white px-4 py-3 shadow-sm"
						:style="{ borderLeftColor: tabColor('nutrition_path'), borderLeftWidth: '4px' }"
					>
						<p class="text-xs font-medium uppercase tracking-wide text-gray-500">
							Scostamento %
						</p>
						<p class="mt-1 text-lg font-semibold text-gray-900">
							{{ nutritionPeriodKpi.pctFormatted ?? '—' }}
						</p>
					</div>
				</div>
				<div class="min-w-0 flex-1">
					<ClientOnly>
						<NutritionProgressChart
							:records="filteredNutritionRecords"
							:param-key="selectedNutritionChartParam"
							:accent-color="tabColor('nutrition_path')"
						/>
						<template #fallback>
							<div class="h-[280px] rounded-none border border-gray-200 bg-gray-50 flex items-center justify-center text-sm text-gray-500">
								Caricamento grafico...
							</div>
						</template>
					</ClientOnly>
				</div>
			</div>
			<MyFocusListSection
				:loading="isNutritionRecordsLoading"
				:error="nutritionRecordsError"
				:empty="!filteredNutritionRecords.length"
				:empty-message="nutritionDateFrom || nutritionDateTo ? 'Nessuna visita nel periodo selezionato.' : 'Nessuna visita nutrizionale registrata.'"
				:current-page="currentNutritionRecordsPage"
				:total-pages="totalNutritionRecordsPages"
				@prev-page="currentNutritionRecordsPage = Math.max(1, currentNutritionRecordsPage - 1)"
				@next-page="currentNutritionRecordsPage = Math.min(totalNutritionRecordsPages, currentNutritionRecordsPage + 1)"
			>
				<MyFocusListItemCard
					v-for="visit in paginatedNutritionRecords"
					:key="visit.id"
					:accent-color="ACCENT_NUTRITION"
					class="cursor-pointer"
					@click="openNutritionRecordDetail(visit.id)"
				>
					<p class="text-sm font-semibold text-gray-900">
						<template v-if="visit.appointment_date">Visita del {{ formatNutritionDate(visit.appointment_date) }}</template>
						<template v-else>Visita (data da definire)</template>
					</p>
					<div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
						<span v-if="visit.total_weight != null">
							Peso: {{ visit.total_weight }} kg
						</span>
					</div>
					<p
						v-if="visit.notes"
						class="mt-1 text-sm text-gray-700 line-clamp-2"
					>
						{{ visit.notes }}
					</p>
				</MyFocusListItemCard>
			</MyFocusListSection>

			<h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide">
				PIANI ALIMENTARI
			</h2>
			<MyFocusListSection
				:loading="isDietsLoading"
				:error="dietsError"
				:empty="!diets.length"
				empty-message="Nessun piano alimentare disponibile."
				:current-page="currentDietsPage"
				:total-pages="totalDietsPages"
				@prev-page="currentDietsPage = Math.max(1, currentDietsPage - 1)"
				@next-page="currentDietsPage = Math.min(totalDietsPages, currentDietsPage + 1)"
			>
				<MyFocusListItemCard
					v-for="diet in paginatedDiets"
					:key="diet.id"
					:accent-color="ACCENT_NUTRITION"
					class="cursor-pointer"
					@click="openDietDetail(diet.id)"
				>
					<p class="text-sm font-semibold text-gray-900">
						{{ diet.title }}
					</p>
					<div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
						<span v-if="diet.start_date && diet.end_date">
							Dal {{ formatDietDate(diet.start_date) }} al {{ formatDietDate(diet.end_date) }}
						</span>
						<span v-else-if="diet.start_date">
							Da {{ formatDietDate(diet.start_date) }}
						</span>
						<span v-else-if="diet.end_date">
							Fino al {{ formatDietDate(diet.end_date) }}
						</span>
						<span v-if="diet.total_kcal != null">
							Kcal totali: {{ diet.total_kcal }}
						</span>
					</div>
					<p
						v-if="diet.description"
						class="mt-1 text-sm text-gray-700 line-clamp-2"
					>
						{{ diet.description }}
					</p>
					<p
						v-else-if="diet.notes"
						class="mt-1 text-sm text-gray-700 line-clamp-2"
					>
						{{ diet.notes }}
					</p>
				</MyFocusListItemCard>
			</MyFocusListSection>
		</div>

		<!-- Tab Psicoterapia -->
		<div v-else-if="activeTab === 'mental_path'" class="space-y-6">
			<h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide">
				SEDUTE PSICOTERAPEUTICHE
			</h2>
			<MyFocusListSection
				:loading="isMentalRecordsLoading"
				:error="mentalRecordsError"
				:empty="!mentalRecords.length"
				empty-message="Nessuna seduta psicoterapeutica disponibile."
				:current-page="currentMentalRecordsPage"
				:total-pages="totalMentalRecordsPages"
				@prev-page="currentMentalRecordsPage = Math.max(1, currentMentalRecordsPage - 1)"
				@next-page="currentMentalRecordsPage = Math.min(totalMentalRecordsPages, currentMentalRecordsPage + 1)"
			>
				<MyFocusListItemCard
					v-for="rec in paginatedMentalRecords"
					:key="rec.id"
					:accent-color="ACCENT_MENTAL"
					class="cursor-pointer"
					@click="openMentalRecordDetail(rec.id)"
				>
					<div class="flex items-start justify-between gap-3">
						<div class="min-w-0 flex-1">
							<p class="text-sm font-semibold text-gray-900">
								<template v-if="rec.appointment_date">
									Seduta del {{ formatNutritionDate(rec.appointment_date) }}
								</template>
								<template v-else>
									Seduta
								</template>
							</p>
							<p
								v-if="rec.notes"
								class="mt-1 text-sm text-gray-700 line-clamp-2"
							>
								{{ rec.notes }}
							</p>
						</div>
						<span
							v-if="rec.sentiment"
							class="material-symbols-outlined shrink-0 text-2xl text-gray-600"
							:aria-label="mentalSentimentLabel(rec.sentiment)"
							:title="mentalSentimentLabel(rec.sentiment)"
						>
							{{ mentalSentimentIcon(rec.sentiment) }}
						</span>
					</div>
				</MyFocusListItemCard>
			</MyFocusListSection>
		</div>

		<!-- Pannello dettaglio piano alimentare -->
		<Teleport to="body">
			<div
				v-if="selectedDietId != null"
				class="fixed inset-0 z-50 flex"
				aria-modal="true"
				role="dialog"
				aria-label="Dettaglio piano alimentare"
			>
				<button
					type="button"
					class="absolute inset-0 bg-black/50 transition-opacity"
					aria-label="Chiudi dettaglio piano alimentare"
					@click="closeDietDetail"
				/>
				<div
					class="relative ml-auto w-full max-w-md sm:max-w-xl bg-white shadow-xl flex flex-col max-h-full overflow-hidden"
					@click.stop
				>
					<div class="flex items-center justify-between shrink-0 px-4 py-3 border-b border-gray-200">
						<h2 class="text-lg font-semibold text-gray-900">
							Dettaglio piano alimentare
						</h2>
						<button
							type="button"
							class="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
							aria-label="Chiudi dettaglio piano alimentare"
							@click="closeDietDetail"
						>
							<span class="material-symbols-outlined">close</span>
						</button>
					</div>

					<div class="flex-1 overflow-y-auto px-4 py-4 space-y-4">
						<div v-if="isDietDetailLoading" class="py-8 text-center text-gray-500">
							Caricamento...
						</div>
						<div v-else-if="dietDetailError" class="py-4 text-sm text-red-600">
							{{ dietDetailError }}
						</div>
						<div v-else-if="selectedDietDetail" class="space-y-4">
							<div
								class="rounded-none border border-gray-200 bg-white shadow-sm px-4 py-4 space-y-2"
								:style="{ borderLeftColor: ACCENT_NUTRITION, borderLeftWidth: '4px' }"
							>
								<p class="text-sm font-semibold text-gray-900">{{ selectedDietDetail.title }}</p>
								<div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
									<span v-if="selectedDietDetail.start_date && selectedDietDetail.end_date">
										Dal {{ formatDietDate(selectedDietDetail.start_date) }} al {{ formatDietDate(selectedDietDetail.end_date) }}
									</span>
									<span v-else-if="selectedDietDetail.start_date">
										Da {{ formatDietDate(selectedDietDetail.start_date) }}
									</span>
									<span v-else-if="selectedDietDetail.end_date">
										Fino al {{ formatDietDate(selectedDietDetail.end_date) }}
									</span>
									<span v-if="selectedDietDetail.total_kcal != null">
										Kcal totali: {{ selectedDietDetail.total_kcal }}
									</span>
								</div>
								<p v-if="selectedDietDetail.description" class="text-sm text-gray-700 whitespace-pre-line">
									{{ selectedDietDetail.description }}
								</p>
								<p v-else-if="selectedDietDetail.notes" class="text-sm text-gray-700 whitespace-pre-line">
									{{ selectedDietDetail.notes }}
								</p>
							</div>

							<div class="rounded-none border border-gray-200 bg-white shadow-sm px-4 py-4 space-y-3">
								<h3 class="text-sm font-semibold text-gray-900 uppercase tracking-wide">
									Parametri dieta
								</h3>
								<dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
									<div>
										<dt class="text-gray-500">TDEE</dt>
										<dd class="text-gray-900 font-medium">{{ formatDietMetric(selectedDietDetail.TDEE, 'kcal') }}</dd>
									</div>
									<div>
										<dt class="text-gray-500">TDEE delta</dt>
										<dd class="text-gray-900 font-medium">{{ formatDietMetric(selectedDietDetail.TDEE_delta, 'kcal') }}</dd>
									</div>
									<div>
										<dt class="text-gray-500">Kcal totali</dt>
										<dd class="text-gray-900 font-medium">{{ formatDietMetric(selectedDietDetail.total_kcal, 'kcal') }}</dd>
									</div>
									<div>
										<dt class="text-gray-500">Acqua</dt>
										<dd class="text-gray-900 font-medium">{{ formatDietMetric(selectedDietDetail.water, 'L') }}</dd>
									</div>
								</dl>

								<div v-if="selectedDietDetail.macros?.length" class="space-y-2 pt-1">
									<p class="text-xs font-semibold uppercase tracking-wide text-gray-600">Macronutrienti</p>
									<div class="overflow-x-auto border border-gray-200">
										<table class="min-w-full text-sm">
											<thead>
												<tr class="bg-gray-100">
													<th class="px-3 py-2 text-left font-semibold text-gray-900 border-b border-r border-gray-200">Macronutriente</th>
													<th class="px-3 py-2 text-right font-semibold text-gray-900 border-b border-r border-gray-200">g/die</th>
													<th class="px-3 py-2 text-right font-semibold text-gray-900 border-b border-r border-gray-200">%</th>
													<th class="px-3 py-2 text-right font-semibold text-gray-900 border-b border-gray-200">g/kg</th>
												</tr>
											</thead>
											<tbody>
												<tr
													v-for="macro in [...selectedDietDetail.macros].sort((a, b) => a.sort - b.sort)"
													:key="macro.id"
													class="odd:bg-white even:bg-gray-50"
												>
													<td class="px-3 py-2 text-gray-900 border-b border-r border-gray-200">{{ macroTypeLabel(macro.macro_type) }}</td>
													<td class="px-3 py-2 text-right text-gray-900 font-medium border-b border-r border-gray-200">{{ formatDietMetric(macro.grams_per_day, 'g') }}</td>
													<td class="px-3 py-2 text-right text-gray-900 border-b border-r border-gray-200">{{ formatDietMetric(macro.percentage, '%') }}</td>
													<td class="px-3 py-2 text-right text-gray-900 border-b border-gray-200">{{ formatDietMetric(macro.grams_per_kg, 'g/kg') }}</td>
												</tr>
											</tbody>
										</table>
									</div>
								</div>
							</div>

							<div
								v-if="selectedDietDetail.combo_meals?.length"
								class="space-y-4"
							>
								<h3 class="text-sm font-semibold text-gray-900 uppercase tracking-wide">
									Pianificazione pasti
								</h3>
								<div
									v-for="meal in [...selectedDietDetail.combo_meals].sort((a, b) => a.sort - b.sort)"
									:key="meal.id"
									class="border border-gray-200 rounded-none px-3 py-3 space-y-3"
								>
									<div>
										<p class="text-sm font-semibold text-gray-900">
											{{ meal.title || 'Pasto' }}<span v-if="meal.info"> ({{ meal.info }})</span>
										</p>
									</div>

									<div v-if="sortComboMealPlans(meal.plans).length" class="space-y-3">
										<div
											v-for="plan in sortComboMealPlans(meal.plans)"
											:key="plan.id"
											class="space-y-2"
										>
											<p
												v-if="getPlanBaseOption(plan)"
												class="text-sm text-gray-700"
											>
												{{ formatOptionFoodsInline(getPlanBaseOption(plan)!) }} +
											</p>
											<p class="text-xs font-semibold uppercase tracking-wide text-gray-600">
												{{ plan.title || 'Pianificazione' }}
											</p>
											<div
												v-for="category in sortPlanCategories(plan.categories).filter((c) => !c.is_hidden)"
												:key="category.id"
												class="space-y-1"
											>
												<p v-if="category.title" class="text-xs font-medium text-gray-700">
													{{ category.title }}
												</p>
												<ul
													v-if="getVisibleCategoryNonBaseOptions(category).length"
													class="space-y-1 text-sm text-gray-800"
												>
													<li
														v-for="option in getVisibleCategoryNonBaseOptions(category)"
														:key="option.id"
														class="leading-relaxed border-b border-gray-200 pb-1 last:border-b-0 last:pb-0"
													>
														<span class="text-gray-700"> {{ formatOptionFoodsInline(option) }}</span>
													</li>
												</ul>
												<p v-else class="text-sm text-gray-500">
													Nessuna opzione compilata.
												</p>
											</div>
											<p
												v-if="!sortPlanCategories(plan.categories).filter((c) => !c.is_hidden).length"
												class="text-sm text-gray-500"
											>
												Nessuna categoria disponibile.
											</p>
										</div>
									</div>
									<p v-else class="text-sm text-gray-500">Nessuna pianificazione disponibile.</p>
								</div>
							</div>

							<div v-if="selectedDietWeekGroups.length" class="space-y-4">
								<h3 class="text-sm font-semibold text-gray-900 uppercase tracking-wide">
									Dieta settimanale
								</h3>
								<div
									v-for="weekGroup in selectedDietWeekGroups"
									:key="weekGroup.week ?? 'week-null'"
									class="space-y-2"
								>
									<p class="text-xs font-semibold uppercase tracking-wide text-gray-600">
										{{ dietWeekLabel(weekGroup.week) }}
									</p>
									<div
										v-for="day in weekGroup.days"
										:key="day.id"
										class="border border-gray-200 rounded-none px-3 py-3 space-y-2"
									>
										<p class="text-sm font-semibold text-gray-900">{{ dietWeekdayLabel(day.weekday) }}</p>
										<ul v-if="getVisibleDayMeals(day).length" class="text-sm text-gray-800">
											<li
												v-for="meal in getVisibleDayMeals(day)"
												:key="meal.id"
												class="py-2 border-b border-gray-200 last:border-b-0 last:pb-0 first:pt-0"
											>
												<p class="font-medium text-gray-900">{{ dietMealTypeLabel(meal.meal_type) }}</p>
												<p class="text-gray-700 leading-relaxed">{{ formatMealFoodsInline(meal) }}</p>
											</li>
										</ul>
									</div>
								</div>
							</div>

							<div
								v-if="selectedDietDetail.notes"
								class="rounded-none border border-gray-200 bg-white shadow-sm px-4 py-4 space-y-2"
							>
								<h3 class="text-sm font-semibold text-gray-900 uppercase tracking-wide">Note</h3>
								<p class="text-sm text-gray-700 whitespace-pre-line">{{ selectedDietDetail.notes }}</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Teleport>

		<!-- Pannello dettaglio visita medica (drawer a destra, fuori dalla catena v-if dei tab) -->
		<Teleport to="body">
			<div
				v-if="selectedHealthRecordId != null"
				class="fixed inset-0 z-50 flex"
				aria-modal="true"
				role="dialog"
				aria-label="Dettaglio visita medica"
			>
				<button
					type="button"
					class="absolute inset-0 bg-black/50 transition-opacity"
					aria-label="Chiudi dettaglio visita"
					@click="closeHealthRecordDetail"
				/>
				<div
					class="relative ml-auto w-full max-w-md sm:max-w-lg bg-white shadow-xl flex flex-col max-h-full overflow-hidden"
					@click.stop
				>
					<div class="flex items-center justify-between shrink-0 px-4 py-3 border-b border-gray-200">
						<h2 class="text-lg font-semibold text-gray-900">
							Dettaglio visita medica
						</h2>
						<button
							type="button"
							class="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
							aria-label="Chiudi dettaglio visita"
							@click="closeHealthRecordDetail"
						>
							<span class="material-symbols-outlined">close</span>
						</button>
					</div>

					<div class="flex-1 overflow-y-auto px-4 py-4 space-y-4">
						<div v-if="isHealthRecordDetailLoading" class="py-8 text-center text-gray-500">
							Caricamento...
						</div>
						<div v-else-if="healthRecordDetailError" class="py-4 text-sm text-red-600">
							{{ healthRecordDetailError }}
						</div>
						<div v-else-if="selectedHealthRecordDetail" class="space-y-4">
							<div
								class="rounded-none border border-gray-200 bg-white shadow-sm px-4 py-4 space-y-3"
								:style="{ borderLeftColor: ACCENT_HEALTH, borderLeftWidth: '4px' }"
							>
								<p class="text-sm font-semibold text-gray-900">
									<template v-if="selectedHealthRecordDetail.appointment_date">
										Visita del {{ formatNutritionDate(selectedHealthRecordDetail.appointment_date) }}
									</template>
									<template v-else>
										Visita medica
									</template>
								</p>
								<dl class="space-y-2 text-sm">
									<div v-if="selectedHealthRecordDetail.visit_reason">
										<dt class="font-medium text-gray-500">Motivo visita</dt>
										<dd class="text-gray-900">{{ selectedHealthRecordDetail.visit_reason }}</dd>
									</div>
									<div v-if="selectedHealthRecordDetail.clinical_picture">
										<dt class="font-medium text-gray-500">Quadro clinico</dt>
										<dd class="text-gray-900 whitespace-pre-line">{{ selectedHealthRecordDetail.clinical_picture }}</dd>
									</div>
									<div v-if="selectedHealthRecordDetail.vas">
										<dt class="font-medium text-gray-500">VAS / Dolore</dt>
										<dd class="text-gray-900 whitespace-pre-line">{{ selectedHealthRecordDetail.vas }}</dd>
									</div>
									<div v-if="selectedHealthRecordDetail.present_diagnostics">
										<dt class="font-medium text-gray-500">Diagnostica presente</dt>
										<dd class="text-gray-900 whitespace-pre-line">{{ selectedHealthRecordDetail.present_diagnostics }}</dd>
									</div>
									<div v-if="selectedHealthRecordDetail.red_flags">
										<dt class="font-medium text-gray-500">Red flag</dt>
										<dd class="text-gray-900 whitespace-pre-line">{{ selectedHealthRecordDetail.red_flags }}</dd>
									</div>
									<div v-if="selectedHealthRecordDetail.medical_history_pharmacology">
										<dt class="font-medium text-gray-500">Anamnesi / Farmacologia</dt>
										<dd class="text-gray-900 whitespace-pre-line">{{ selectedHealthRecordDetail.medical_history_pharmacology }}</dd>
									</div>
									<div v-if="selectedHealthRecordDetail.notes">
										<dt class="font-medium text-gray-500">Note</dt>
										<dd class="text-gray-900 whitespace-pre-line">{{ selectedHealthRecordDetail.notes }}</dd>
									</div>
								</dl>
							</div>

							<div v-if="selectedHealthRecordDetail.report_files?.length" class="text-sm space-y-2">
								<h4 class="font-medium text-gray-900">Referti / File</h4>
								<ul class="space-y-3">
									<li
										v-for="file in selectedHealthRecordDetail.report_files"
										:key="file.id"
									>
										<a
											:href="healthReportFileDownloadUrl(file.id) || undefined"
											target="_blank"
											rel="noopener noreferrer"
											class="relative flex items-center gap-3 border border-gray-200 bg-white shadow-sm hover:shadow-md rounded-none px-4 py-3 transition-shadow"
										>
											<span
												class="material-symbols-outlined text-lg text-gray-700 shrink-0"
												aria-hidden="true"
											>
												description
											</span>
											<div class="min-w-0 flex-1">
												<p class="text-sm font-medium text-gray-900 truncate">
													{{ file.filename_download || 'Documento' }}
												</p>
												<p class="text-xs text-gray-600">
													Clicca per scaricare
												</p>
											</div>
											<span
												class="material-symbols-outlined text-lg text-gray-700 shrink-0"
												aria-hidden="true"
											>
												download
											</span>
										</a>
									</li>
								</ul>
							</div>
							<p
								v-else
								class="text-sm text-gray-500"
							>
								Nessun file referto associato a questa visita.
							</p>
						</div>
					</div>
				</div>
			</div>
		</Teleport>

		<!-- Pannello creazione nuovo referto medico -->
		<Teleport to="body">
			<div
				v-if="isCreateMedicalReportOpen"
				class="fixed inset-0 z-50 flex"
				aria-modal="true"
				role="dialog"
				aria-label="Carica nuovo referto medico"
			>
				<button
					type="button"
					class="absolute inset-0 bg-black/50 transition-opacity"
					aria-label="Chiudi pannello creazione referto"
					@click="isCreateMedicalReportOpen = false"
				/>
				<div
					class="relative ml-auto w-full max-w-md sm:max-w-lg bg-white shadow-xl flex flex-col max-h-full overflow-hidden"
					@click.stop
				>
					<div class="flex items-center justify-between shrink-0 px-4 py-3 border-b border-gray-200">
						<h2 class="text-lg font-semibold text-gray-900">
							Carica referto medico
						</h2>
						<button
							type="button"
							class="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
							aria-label="Chiudi pannello creazione referto"
							@click="isCreateMedicalReportOpen = false"
						>
							<span class="material-symbols-outlined">close</span>
						</button>
					</div>

					<form class="flex-1 overflow-y-auto px-4 py-4 space-y-4" @submit.prevent="submitCreateMedicalReport">
						<p class="text-sm text-gray-600">
							Compila i campi obbligatori contrassegnati con * e allega almeno un file. Il referto sarà associato automaticamente al tuo profilo.
						</p>

						<div class="space-y-1">
							<label for="medical-report-date" class="block text-sm font-medium text-gray-700">
								Data referto *
							</label>
							<Input
								id="medical-report-date"
								v-model="createMedicalReportDate"
								type="date"
								class="relative pr-10 border border-gray-300 bg-background"
								:class="createMedicalReportDateInputClasses"
							/>
							<p
								v-if="createMedicalReportDateError"
								class="mt-1 text-xs text-red-600"
							>
								{{ createMedicalReportDateError }}
							</p>
						</div>

						<div class="space-y-1">
							<label for="medical-report-description" class="block text-sm font-medium text-gray-700">
								Descrizione *
							</label>
							<textarea
								id="medical-report-description"
								v-model="createMedicalReportDescription"
								class="mt-1 block w-full rounded-md bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2"
								:class="createMedicalReportDescriptionError
									? 'border border-red-500 focus-visible:border-red-500 focus-visible:ring-red-500'
									: 'border border-gray-300 focus-visible:border-[#A21B1F] focus-visible:ring-[#A21B1F]'"
								rows="3"
							/>
							<p
								v-if="createMedicalReportDescriptionError"
								class="mt-1 text-xs text-red-600"
							>
								{{ createMedicalReportDescriptionError }}
							</p>
						</div>

						<div class="space-y-1">
							<label for="medical-report-notes" class="block text-sm font-medium text-gray-700">
								Note
							</label>
							<textarea
								id="medical-report-notes"
								v-model="createMedicalReportNotes"
								class="mt-1 block w-full rounded-md border border-gray-300 bg-background px-3 py-2 text-sm shadow-sm focus:border-[#A21B1F] focus:ring-[#A21B1F] focus-visible:outline-none"
								rows="3"
								placeholder="Eventuali note aggiuntive sul referto"
							/>
						</div>

						<div class="space-y-1">
							<label for="medical-report-files" class="block text-sm font-medium text-gray-700">
								File allegati * (almeno uno)
							</label>
							<div class="mt-1 flex flex-wrap items-center gap-3">
								<label
									for="medical-report-files"
									class="inline-flex items-center justify-center gap-2 h-9 px-4 py-2 text-xs sm:text-sm font-medium font-button text-black bg-white border-2 border-black hover:bg-gray-50 hover:text-[#A21B1F] rounded-none transition-colors uppercase cursor-pointer"
								>
									<span class="material-symbols-outlined text-base sm:text-lg" aria-hidden="true">upload_file</span>
									SCEGLI FILE
								</label>
								<input
									id="medical-report-files"
									type="file"
									accept=".pdf,image/*"
									multiple
									class="sr-only"
									@change="onCreateMedicalReportFilesChange"
								/>
								<label
									for="medical-report-camera"
									class="inline-flex items-center justify-center gap-2 h-9 px-4 py-2 text-xs sm:text-sm font-medium font-button text-white bg-black hover:bg-black/90 rounded-none transition-colors uppercase cursor-pointer sm:hidden"
								>
									<span class="material-symbols-outlined text-base sm:text-lg" aria-hidden="true">photo_camera</span>
									SCATTA FOTO
								</label>
								<input
									id="medical-report-camera"
									type="file"
									accept="image/*"
									capture="environment"
									multiple
									class="sr-only"
									@change="onCreateMedicalReportCameraChange"
								/>
								<p
									class="text-xs"
									:class="createMedicalReportFilesError ? 'text-red-600' : 'text-gray-600'"
								>
									<span v-if="createMedicalReportFiles.length">
										File selezionati: {{ createMedicalReportFiles.length }}
									</span>
									<span v-else>
										Nessun file selezionato
									</span>
								</p>
							</div>
							<p class="mt-1 text-xs text-gray-500">
								Puoi caricare PDF o immagini (JPG, PNG, ...).
							</p>
							<ul
								v-if="createMedicalReportFiles.length"
								class="mt-2 space-y-2"
							>
								<li
									v-for="upload in createMedicalReportFiles"
									:key="upload.id"
									class="flex items-center gap-3 border border-gray-200 bg-white px-3 py-2"
								>
									<div class="shrink-0">
										<img
											v-if="upload.previewUrl"
											:src="upload.previewUrl"
											:alt="upload.file.name"
											class="h-12 w-12 object-cover border border-gray-200"
										>
										<span
											v-else
											class="material-symbols-outlined text-gray-500"
											aria-hidden="true"
										>
											description
										</span>
									</div>
									<div class="min-w-0 flex-1">
										<p class="truncate text-sm text-gray-900">
											{{ upload.file.name }}
										</p>
										<p class="text-xs text-gray-500">
											{{ Math.max(1, Math.round(upload.file.size / 1024)) }} KB
										</p>
									</div>
									<button
										type="button"
										class="inline-flex items-center justify-center h-8 px-3 text-xs font-medium font-button text-black bg-white border-2 border-black hover:bg-gray-50 hover:text-[#A21B1F] rounded-none transition-colors uppercase"
										@click="removeCreateMedicalReportFile(upload.id)"
									>
										Rimuovi
									</button>
								</li>
							</ul>
						</div>

						<p
							v-if="createMedicalReportError"
							class="text-sm text-red-600"
						>
							{{ createMedicalReportError }}
						</p>

						<div class="flex items-center justify-end gap-3 pt-2">
							<button
								type="button"
								class="inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-sm font-medium font-button text-black bg-white border-2 border-black hover:bg-gray-50 hover:text-[#A21B1F] rounded-none transition-colors uppercase"
								@click="isCreateMedicalReportOpen = false"
								:disabled="isCreateMedicalReportSubmitting"
							>
								Annulla
							</button>
							<button
								type="submit"
								class="inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-sm font-medium font-button text-white bg-[#A21B1F] hover:bg-[#A21B1F]/90 rounded-none transition-colors uppercase disabled:opacity-50 disabled:cursor-not-allowed"
								:disabled="isCreateMedicalReportSubmitting"
							>
								<span
									v-if="isCreateMedicalReportSubmitting"
									class="material-symbols-outlined animate-spin text-lg"
									aria-hidden="true"
								>
									progress_activity
								</span>
								<span v-else class="material-symbols-outlined text-lg" aria-hidden="true">
									upload_file
								</span>
								<span>
									{{ isCreateMedicalReportSubmitting ? 'Salvataggio...' : 'Salva referto' }}
								</span>
							</button>
						</div>
					</form>
				</div>
			</div>
		</Teleport>

		<!-- Pannello dettaglio referto medico -->
		<Teleport to="body">
			<div
				v-if="selectedMedicalReportId != null"
				class="fixed inset-0 z-50 flex"
				aria-modal="true"
				role="dialog"
				aria-label="Dettaglio referto medico"
			>
				<button
					type="button"
					class="absolute inset-0 bg-black/50 transition-opacity"
					aria-label="Chiudi dettaglio referto"
					@click="closeMedicalReportDetail"
				/>
				<div
					class="relative ml-auto w-full max-w-md sm:max-w-lg bg-white shadow-xl flex flex-col max-h-full overflow-hidden"
					@click.stop
				>
					<div class="flex items-center justify-between shrink-0 px-4 py-3 border-b border-gray-200">
						<h2 class="text-lg font-semibold text-gray-900">
							Dettaglio referto medico
						</h2>
						<button
							type="button"
							class="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
							aria-label="Chiudi dettaglio referto"
							@click="closeMedicalReportDetail"
						>
							<span class="material-symbols-outlined">close</span>
						</button>
					</div>

					<div class="flex-1 overflow-y-auto px-4 py-4 space-y-4">
						<div v-if="isMedicalReportDetailLoading" class="py-8 text-center text-gray-500">
							Caricamento...
						</div>
						<div v-else-if="medicalReportDetailError" class="py-4 text-sm text-red-600">
							{{ medicalReportDetailError }}
						</div>
						<div v-else-if="selectedMedicalReportDetail" class="space-y-4">
							<div
								class="rounded-none border border-gray-200 bg-white shadow-sm px-4 py-4 space-y-3"
								:style="{ borderLeftColor: ACCENT_HEALTH, borderLeftWidth: '4px' }"
							>
								<p class="text-sm font-semibold text-gray-900">
									<template v-if="selectedMedicalReportDetail.date">
										Referto del {{ formatNutritionDate(selectedMedicalReportDetail.date) }}
									</template>
									<template v-else>
										Referto medico
									</template>
								</p>
								<dl class="space-y-2 text-sm">
									<div v-if="selectedMedicalReportDetail.description">
										<dt class="font-medium text-gray-500">Descrizione</dt>
										<dd class="text-gray-900 whitespace-pre-line">{{ selectedMedicalReportDetail.description }}</dd>
									</div>
									<div v-if="selectedMedicalReportDetail.notes">
										<dt class="font-medium text-gray-500">Note</dt>
										<dd class="text-gray-900 whitespace-pre-line">{{ selectedMedicalReportDetail.notes }}</dd>
									</div>
								</dl>
							</div>

							<div v-if="selectedMedicalReportDetail.report_files?.length" class="text-sm space-y-2">
								<h4 class="font-medium text-gray-900">File allegati</h4>
								<ul class="space-y-3">
									<li
										v-for="file in selectedMedicalReportDetail.report_files"
										:key="file.id"
									>
										<a
											:href="healthReportFileDownloadUrl(file.id) || undefined"
											target="_blank"
											rel="noopener noreferrer"
											class="relative flex items-center gap-3 border border-gray-200 bg-white shadow-sm hover:shadow-md rounded-none px-4 py-3 transition-shadow"
										>
											<span
												class="material-symbols-outlined text-lg text-gray-700 shrink-0"
												aria-hidden="true"
											>
												description
											</span>
											<div class="min-w-0 flex-1">
												<p class="text-sm font-medium text-gray-900 truncate">
													{{ file.filename_download || 'Documento' }}
												</p>
												<p class="text-xs text-gray-600">
													Clicca per scaricare
												</p>
											</div>
											<span
												class="material-symbols-outlined text-lg text-gray-700 shrink-0"
												aria-hidden="true"
											>
												download
											</span>
										</a>
									</li>
								</ul>
							</div>
							<p
								v-else
								class="text-sm text-gray-500"
							>
								Nessun file allegato a questo referto.
							</p>
						</div>
					</div>
				</div>
			</div>
		</Teleport>

		<!-- Pannello dettaglio visita nutrizionale -->
		<Teleport to="body">
			<div
				v-if="selectedNutritionRecordId != null"
				class="fixed inset-0 z-50 flex"
				aria-modal="true"
				role="dialog"
				aria-label="Dettaglio visita nutrizionale"
			>
				<button
					type="button"
					class="absolute inset-0 bg-black/50 transition-opacity"
					aria-label="Chiudi dettaglio visita"
					@click="closeNutritionRecordDetail"
				/>
				<div
					class="relative ml-auto w-full max-w-md sm:max-w-lg bg-white shadow-xl flex flex-col max-h-full overflow-hidden"
					@click.stop
				>
					<div class="flex items-center justify-between shrink-0 px-4 py-3 border-b border-gray-200">
						<h2 class="text-lg font-semibold text-gray-900">
							Dettaglio visita nutrizionale
						</h2>
						<button
							type="button"
							class="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
							aria-label="Chiudi dettaglio visita"
							@click="closeNutritionRecordDetail"
						>
							<span class="material-symbols-outlined">close</span>
						</button>
					</div>

					<div class="flex-1 overflow-y-auto px-4 py-4 space-y-4">
						<div v-if="isNutritionRecordDetailLoading" class="py-8 text-center text-gray-500">
							Caricamento...
						</div>
						<div v-else-if="nutritionRecordDetailError" class="py-4 text-sm text-red-600">
							{{ nutritionRecordDetailError }}
						</div>
						<div v-else-if="selectedNutritionRecordDetail" class="space-y-4">
							<!-- Intestazione visita -->
							<div
								class="rounded-none border border-gray-200 bg-white shadow-sm px-4 py-4"
								:style="{ borderLeftColor: ACCENT_NUTRITION, borderLeftWidth: '4px' }"
							>
								<p class="text-sm font-semibold text-gray-900">
									<template v-if="selectedNutritionRecordDetail.appointment_date">
										Visita del {{ formatNutritionDate(selectedNutritionRecordDetail.appointment_date) }}
									</template>
									<template v-else>
										Visita nutrizionale
									</template>
								</p>
								<p
									v-if="selectedNutritionRecordDetail.notes"
									class="mt-2 text-sm text-gray-700 whitespace-pre-line"
								>
									{{ selectedNutritionRecordDetail.notes }}
								</p>
							</div>

							<!-- Composizione corporea -->
							<div
								v-if="[selectedNutritionRecordDetail.total_weight, selectedNutritionRecordDetail.body_fat_percentage, selectedNutritionRecordDetail.total_lean_mass, selectedNutritionRecordDetail.skeletal_muscle_mass, selectedNutritionRecordDetail.total_body_water, selectedNutritionRecordDetail.intracellular_water].some(v => v != null)"
								class="space-y-2"
							>
								<h3 class="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2">
									Composizione corporea
								</h3>
								<dl class="grid gap-x-4 gap-y-2 text-sm">
									<div v-if="selectedNutritionRecordDetail.total_weight != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">Peso totale (kg)</dt>
										<dd class="font-medium text-gray-900">{{ formatDetailNum(selectedNutritionRecordDetail.total_weight) }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.body_fat_percentage != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">% Massa grassa</dt>
										<dd class="font-medium text-gray-900">{{ formatDetailNum(selectedNutritionRecordDetail.body_fat_percentage) }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.total_lean_mass != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">Massa magra totale (kg)</dt>
										<dd class="font-medium text-gray-900">{{ formatDetailNum(selectedNutritionRecordDetail.total_lean_mass) }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.skeletal_muscle_mass != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">Massa muscolare scheletrica (kg)</dt>
										<dd class="font-medium text-gray-900">{{ formatDetailNum(selectedNutritionRecordDetail.skeletal_muscle_mass) }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.total_body_water != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">Acqua corporea totale (l)</dt>
										<dd class="font-medium text-gray-900">{{ formatDetailNum(selectedNutritionRecordDetail.total_body_water) }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.intracellular_water != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">Acqua intracellulare (l)</dt>
										<dd class="font-medium text-gray-900">{{ formatDetailNum(selectedNutritionRecordDetail.intracellular_water) }}</dd>
									</div>
								</dl>
							</div>

							<!-- Misurazione Circonferenze -->
							<div
								v-if="[selectedNutritionRecordDetail.circ_chest, selectedNutritionRecordDetail.circ_waist, selectedNutritionRecordDetail.circ_abdomen, selectedNutritionRecordDetail.circ_hips, selectedNutritionRecordDetail.circ_arm_relaxed, selectedNutritionRecordDetail.circ_arm_contracted, selectedNutritionRecordDetail.circ_forearm, selectedNutritionRecordDetail.circ_thigh, selectedNutritionRecordDetail.circ_calf].some(v => v != null)"
								class="space-y-2 pt-2 border-t border-gray-200"
							>
								<h3 class="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2">
									Misurazione Circonferenze
								</h3>
								<dl class="grid gap-x-4 gap-y-2 text-sm">
									<div v-if="selectedNutritionRecordDetail.circ_chest != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">Torace (cm) <span class="text-gray-400 font-normal">A inspirazione normale</span></dt>
										<dd class="font-medium text-gray-900 shrink-0">{{ formatDetailNum(selectedNutritionRecordDetail.circ_chest) }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.circ_waist != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">Vita (cm) <span class="text-gray-400 font-normal">nel punto più stretto</span></dt>
										<dd class="font-medium text-gray-900 shrink-0">{{ formatDetailNum(selectedNutritionRecordDetail.circ_waist) }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.circ_abdomen != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">Addome (cm) <span class="text-gray-400 font-normal">a livello ombelicale</span></dt>
										<dd class="font-medium text-gray-900 shrink-0">{{ formatDetailNum(selectedNutritionRecordDetail.circ_abdomen) }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.circ_hips != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">Fianchi / glutei (cm)</dt>
										<dd class="font-medium text-gray-900">{{ formatDetailNum(selectedNutritionRecordDetail.circ_hips) }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.circ_arm_relaxed != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">Braccio rilassato (cm)</dt>
										<dd class="font-medium text-gray-900">{{ formatDetailNum(selectedNutritionRecordDetail.circ_arm_relaxed) }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.circ_arm_contracted != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">Braccio contratto (cm)</dt>
										<dd class="font-medium text-gray-900">{{ formatDetailNum(selectedNutritionRecordDetail.circ_arm_contracted) }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.circ_forearm != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">Avambraccio (cm)</dt>
										<dd class="font-medium text-gray-900">{{ formatDetailNum(selectedNutritionRecordDetail.circ_forearm) }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.circ_thigh != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">Coscia (cm) <span class="text-gray-400 font-normal">A metà femore</span></dt>
										<dd class="font-medium text-gray-900 shrink-0">{{ formatDetailNum(selectedNutritionRecordDetail.circ_thigh) }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.circ_calf != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">Polpaccio (cm)</dt>
										<dd class="font-medium text-gray-900">{{ formatDetailNum(selectedNutritionRecordDetail.circ_calf) }}</dd>
									</div>
								</dl>
							</div>

							<!-- Rapporti e proporzioni -->
							<div
								v-if="[selectedNutritionRecordDetail.ratio_waist_shoulders, selectedNutritionRecordDetail.ratio_chest_waist, selectedNutritionRecordDetail.ratio_arms_calves, selectedNutritionRecordDetail.ratio_thigh_waist].some(v => v != null)"
								class="space-y-2 pt-2 border-t border-gray-200"
							>
								<h3 class="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2">
									Rapporti e proporzioni
								</h3>
								<dl class="grid gap-x-4 gap-y-2 text-sm">
									<div v-if="selectedNutritionRecordDetail.ratio_waist_shoulders != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">Rapporto Vita/Spalle</dt>
										<dd class="font-medium text-gray-900">{{ formatDetailNum(selectedNutritionRecordDetail.ratio_waist_shoulders) }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.ratio_chest_waist != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">Rapporto Torace/Vita</dt>
										<dd class="font-medium text-gray-900 shrink-0">{{ formatDetailNum(selectedNutritionRecordDetail.ratio_chest_waist) }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.ratio_arms_calves != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">Rapporto Braccia/Polpacci</dt>
										<dd class="font-medium text-gray-900">{{ formatDetailNum(selectedNutritionRecordDetail.ratio_arms_calves) }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.ratio_thigh_waist != null" class="flex justify-between gap-2">
										<dt class="text-gray-600">Rapporto Coscia/Vita</dt>
										<dd class="font-medium text-gray-900">{{ formatDetailNum(selectedNutritionRecordDetail.ratio_thigh_waist) }}</dd>
									</div>
								</dl>
							</div>

							<!-- Esami ematici -->
							<div
								v-if="selectedNutritionRecordDetail.complete_blood_count || selectedNutritionRecordDetail.lipid_profile || selectedNutritionRecordDetail.blood_glucose_insulin || selectedNutritionRecordDetail.thyroid_hormones || selectedNutritionRecordDetail.testosterone_cortisol || selectedNutritionRecordDetail.vitamin_d || selectedNutritionRecordDetail.ferritin"
								class="space-y-2 pt-2 border-t border-gray-200"
							>
								<h3 class="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2">
									Esami ematici
								</h3>
								<dl class="grid gap-x-4 gap-y-2 text-sm">
									<div v-if="selectedNutritionRecordDetail.complete_blood_count" class="flex justify-between gap-2">
										<dt class="text-gray-600">Emocromo</dt>
										<dd class="text-gray-900 text-right whitespace-pre-line min-w-0 max-w-[60%]">{{ selectedNutritionRecordDetail.complete_blood_count }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.lipid_profile" class="flex justify-between gap-2">
										<dt class="text-gray-600">Profilo lipidico</dt>
										<dd class="text-gray-900 text-right whitespace-pre-line min-w-0 max-w-[60%]">{{ selectedNutritionRecordDetail.lipid_profile }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.blood_glucose_insulin" class="flex justify-between gap-2">
										<dt class="text-gray-600">Glicemia/Insulina</dt>
										<dd class="text-gray-900 text-right whitespace-pre-line min-w-0 max-w-[60%]">{{ selectedNutritionRecordDetail.blood_glucose_insulin }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.thyroid_hormones" class="flex justify-between gap-2">
										<dt class="text-gray-600">Ormoni tiroide (TSH, FT3, FT4)</dt>
										<dd class="text-gray-900 text-right whitespace-pre-line min-w-0 max-w-[60%]">{{ selectedNutritionRecordDetail.thyroid_hormones }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.testosterone_cortisol" class="flex justify-between gap-2">
										<dt class="text-gray-600">Testosterone/Cortisolo</dt>
										<dd class="text-gray-900 text-right whitespace-pre-line min-w-0 max-w-[60%]">{{ selectedNutritionRecordDetail.testosterone_cortisol }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.vitamin_d" class="flex justify-between gap-2">
										<dt class="text-gray-600">Vitamina D</dt>
										<dd class="text-gray-900 text-right whitespace-pre-line min-w-0 max-w-[60%]">{{ selectedNutritionRecordDetail.vitamin_d }}</dd>
									</div>
									<div v-if="selectedNutritionRecordDetail.ferritin" class="flex justify-between gap-2">
										<dt class="text-gray-600">Ferritina</dt>
										<dd class="text-gray-900 text-right whitespace-pre-line min-w-0 max-w-[60%]">{{ selectedNutritionRecordDetail.ferritin }}</dd>
									</div>
								</dl>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Teleport>

		<!-- Pannello dettaglio seduta psicoterapeutica -->
		<Teleport to="body">
			<div
				v-if="selectedMentalRecordId != null"
				class="fixed inset-0 z-50 flex"
				aria-modal="true"
				role="dialog"
				aria-label="Dettaglio seduta psicoterapeutica"
			>
				<button
					type="button"
					class="absolute inset-0 bg-black/50 transition-opacity"
					aria-label="Chiudi dettaglio seduta"
					@click="closeMentalRecordDetail"
				/>
				<div
					class="relative ml-auto w-full max-w-md sm:max-w-lg bg-white shadow-xl flex flex-col max-h-full overflow-hidden"
					@click.stop
				>
					<div class="flex items-center justify-between shrink-0 px-4 py-3 border-b border-gray-200">
						<h2 class="text-lg font-semibold text-gray-900">
							Dettaglio seduta psicoterapeutica
						</h2>
						<button
							type="button"
							class="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
							aria-label="Chiudi dettaglio seduta"
							@click="closeMentalRecordDetail"
						>
							<span class="material-symbols-outlined">close</span>
						</button>
					</div>

					<div class="flex-1 overflow-y-auto px-4 py-4 space-y-4">
						<div v-if="isMentalRecordDetailLoading" class="py-8 text-center text-gray-500">
							Caricamento...
						</div>
						<div v-else-if="mentalRecordDetailError" class="py-4 text-sm text-red-600">
							{{ mentalRecordDetailError }}
						</div>
						<div v-else-if="selectedMentalRecordDetail" class="space-y-4">
							<div
								class="rounded-none border border-gray-200 bg-white shadow-sm px-4 py-4 flex items-start gap-4"
								:style="{ borderLeftColor: ACCENT_MENTAL, borderLeftWidth: '4px' }"
							>
								<div class="min-w-0 flex-1">
									<p class="text-sm font-semibold text-gray-900">
										<template v-if="selectedMentalRecordDetail.appointment_date">
											Seduta del {{ formatNutritionDate(selectedMentalRecordDetail.appointment_date) }}
										</template>
										<template v-else>
											Seduta psicoterapeutica
										</template>
									</p>
									<div v-if="selectedMentalRecordDetail.notes" class="mt-2 text-sm">
										<p class="text-gray-600 font-medium">Note</p>
										<p class="mt-0.5 text-gray-900 whitespace-pre-line">{{ selectedMentalRecordDetail.notes }}</p>
									</div>
								</div>
								<span
									v-if="selectedMentalRecordDetail.sentiment"
									class="material-symbols-outlined shrink-0 text-3xl text-gray-500"
									:aria-hidden="true"
								>
									{{ mentalSentimentIcon(selectedMentalRecordDetail.sentiment) }}
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Teleport>

		<!-- Pannello dettaglio valutazione sportiva -->
		<Teleport to="body">
			<div
				v-if="selectedSportRecordId != null"
				class="fixed inset-0 z-50 flex"
				aria-modal="true"
				role="dialog"
				aria-label="Dettaglio valutazione sportiva"
			>
				<button
					type="button"
					class="absolute inset-0 bg-black/50 transition-opacity"
					aria-label="Chiudi dettaglio valutazione"
					@click="closeSportRecordDetail"
				/>
				<div
					class="relative ml-auto w-full max-w-md sm:max-w-lg bg-white shadow-xl flex flex-col max-h-full overflow-hidden"
					@click.stop
				>
					<div class="flex items-center justify-between shrink-0 px-4 py-3 border-b border-gray-200">
						<h2 class="text-lg font-semibold text-gray-900">
							Dettaglio valutazione sportiva
						</h2>
						<button
							type="button"
							class="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
							aria-label="Chiudi dettaglio valutazione"
							@click="closeSportRecordDetail"
						>
							<span class="material-symbols-outlined">close</span>
						</button>
					</div>

					<div class="flex-1 overflow-y-auto px-4 py-4 space-y-4">
						<div v-if="isSportRecordDetailLoading" class="py-8 text-center text-gray-500">
							Caricamento...
						</div>
						<div v-else-if="sportRecordDetailError" class="py-4 text-sm text-red-600">
							{{ sportRecordDetailError }}
						</div>
						<div v-else-if="selectedSportRecordDetail" class="space-y-4">
							<div
								class="rounded-none border border-gray-200 bg-white shadow-sm px-4 py-4"
								:style="{ borderLeftColor: ACCENT_FITNESS, borderLeftWidth: '4px' }"
							>
								<p class="text-sm font-semibold text-gray-900">
									<template v-if="selectedSportRecordDetail.appointment_date">
										Valutazione del {{ formatNutritionDate(selectedSportRecordDetail.appointment_date) }}
									</template>
									<template v-else>
										Valutazione sportiva
									</template>
								</p>
								<p
									v-if="selectedSportRecordDetail.notes"
									class="mt-2 text-sm text-gray-700 whitespace-pre-line"
								>
									{{ selectedSportRecordDetail.notes }}
								</p>
							</div>

							<template v-for="cat in selectedSportRecordDetail.evaluation_types" :key="cat.id">
								<div class="space-y-2 pt-2">
									<h3 class="text-sm font-semibold text-gray-900 uppercase tracking-wide">
										{{ cat.evaluation_type_title || 'Categoria valutazione' }}
									</h3>
									<div
										v-for="sec in cat.sections"
										:key="sec.id"
										class="rounded-none border border-gray-200 bg-white shadow-sm px-3 py-3 space-y-2"
									>
										<p class="text-xs font-medium text-gray-600 uppercase tracking-wide">
											{{ sec.section_title || 'Sezione' }}
										</p>
										<ul class="space-y-1.5 text-sm">
											<li
												v-for="ex in sec.exercises"
												:key="ex.id"
												class="flex justify-between gap-2 py-1 border-b border-gray-100 last:border-0"
											>
												<span class="text-gray-900 font-medium shrink-0 max-w-[45%]">
													{{ ex.exercise_name || 'Esercizio' }}
												</span>
												<span class="text-gray-600 text-right min-w-0 max-w-[55%]">
													{{ sportExerciseDisplayValues(ex) }}
												</span>
											</li>
										</ul>
									</div>
								</div>
							</template>
							<p
								v-if="!selectedSportRecordDetail.evaluation_types?.length"
								class="text-sm text-gray-500"
							>
								Nessuna categoria di valutazione registrata.
							</p>
						</div>
					</div>
				</div>
			</div>
		</Teleport>

		<!-- Pannello dettaglio piano di allenamento -->
		<Teleport to="body">
			<div
				v-if="selectedTrainingPlanId != null"
				class="fixed inset-0 z-50 flex"
				aria-modal="true"
				role="dialog"
				aria-label="Dettaglio piano di allenamento"
			>
				<button
					type="button"
					class="absolute inset-0 bg-black/50 transition-opacity"
					aria-label="Chiudi dettaglio piano"
					@click="closeTrainingPlanDetail"
				/>
				<div
					class="relative ml-auto w-full max-w-md sm:max-w-lg bg-white shadow-xl flex flex-col max-h-full overflow-hidden"
					@click.stop
				>
					<div class="flex items-center justify-between shrink-0 px-4 py-3 border-b border-gray-200">
						<h2 class="text-lg font-semibold text-gray-900">
							Dettaglio piano di allenamento
						</h2>
						<button
							type="button"
							class="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
							aria-label="Chiudi dettaglio piano"
							@click="closeTrainingPlanDetail"
						>
							<span class="material-symbols-outlined">close</span>
						</button>
					</div>

					<div class="flex-1 overflow-y-auto px-4 py-4 space-y-4">
						<div v-if="isTrainingPlanDetailLoading" class="py-8 text-center text-gray-500">
							Caricamento...
						</div>
						<div v-else-if="trainingPlanDetailError" class="py-4 text-sm text-red-600">
							{{ trainingPlanDetailError }}
						</div>
						<div v-else-if="selectedTrainingPlanDetail" class="space-y-4">
							<!-- In alto: card del piano come in lista + descrizione -->
							<div
								class="rounded-none border border-gray-200 bg-white shadow-sm pl-4 pr-4 py-3"
								:style="{ borderLeftColor: ACCENT_FITNESS, borderLeftWidth: '4px' }"
							>
								<div class="flex items-start justify-between gap-3">
									<p class="text-sm font-semibold text-gray-900 min-w-0">
										{{ selectedTrainingPlanDetail.title }}
									</p>
									<span
										v-if="trainingPlanStatusLabel(selectedTrainingPlanDetail.status)"
										class="inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium shrink-0"
										:class="getTrainingPlanStatusChipClass(selectedTrainingPlanDetail.status)"
									>
										{{ trainingPlanStatusLabel(selectedTrainingPlanDetail.status) }}
									</span>
								</div>
								<div class="mt-1 flex items-center justify-between gap-3 text-xs text-gray-600">
									<span v-if="selectedTrainingPlanDetail.cycles?.length">
										Microcicli: {{ selectedTrainingPlanDetail.cycles.length }}
									</span>
									<span
										v-if="trainingPlanPhaseLabel(selectedTrainingPlanDetail.phase)"
										class="shrink-0"
									>
										Fase: {{ trainingPlanPhaseLabel(selectedTrainingPlanDetail.phase) }}
									</span>
								</div>
								<div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
									<span v-if="selectedTrainingPlanDetail.start_date && selectedTrainingPlanDetail.end_date">
										Dal {{ formatDietDate(selectedTrainingPlanDetail.start_date) }} al {{ formatDietDate(selectedTrainingPlanDetail.end_date) }}
									</span>
									<span v-else-if="selectedTrainingPlanDetail.start_date">
										Da {{ formatDietDate(selectedTrainingPlanDetail.start_date) }}
									</span>
									<span v-else-if="selectedTrainingPlanDetail.end_date">
										Fino al {{ formatDietDate(selectedTrainingPlanDetail.end_date) }}
									</span>
								</div>
								<p
									v-if="selectedTrainingPlanDetail.description"
									class="mt-2 text-sm text-gray-700 whitespace-pre-line"
								>
									{{ selectedTrainingPlanDetail.description }}
								</p>
							</div>

							<!-- Sotto: una card per ogni microciclo, accordion per settimana, dentro ogni settimana esercizi e set/ripetizioni -->
							<div
								v-for="cycle in selectedTrainingPlanDetail.cycles"
								:key="cycle.id"
								class="rounded-none border border-gray-200 bg-white shadow-sm overflow-hidden"
							>
								<div class="px-4 py-3 border-b border-gray-100 bg-gray-50">
									<h3 class="text-sm font-semibold text-gray-900">
										{{ cycle.title || `Microciclo #${cycle.id}` }}
									</h3>
								</div>
								<div class="divide-y divide-gray-100">
									<div
										v-for="week in cycle.weeks"
										:key="week.id"
										class="border-0"
									>
										<button
											type="button"
											class="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
											@click="toggleTrainingPlanCycleWeek(cycle.id, week.id)"
										>
											<span class="text-sm font-medium text-gray-900">
												{{ week.name || `Settimana ${week.id}` }}
											</span>
											<span
												class="material-symbols-outlined text-gray-500 shrink-0 transition-transform"
												:class="{ 'rotate-180': isTrainingPlanCycleWeekOpen(cycle.id, week.id) }"
											>
												expand_more
											</span>
										</button>
										<div
											v-show="isTrainingPlanCycleWeekOpen(cycle.id, week.id)"
											class="px-4 pb-4 pt-0 space-y-3 bg-gray-50/50"
										>
											<template v-for="block in cycle.blocks" :key="block.id">
												<div
													v-if="block.exercises?.length"
													class="rounded-none border border-gray-200 bg-white px-3 py-3 shadow-sm text-sm"
												>
													<p class="font-medium text-gray-700 mb-1.5">
														{{ block.block_name || 'Blocco' }}
													</p>
													<ul class="space-y-1.5">
														<li
															v-for="ex in block.exercises"
															:key="ex.id"
															class="flex justify-between gap-2 py-1.5 px-2 rounded border border-gray-100 bg-gray-50/80 items-center"
														>
															<span class="text-gray-900 font-medium min-w-0 flex items-center gap-1.5">
																{{ ex.exercise_name || 'Esercizio' }}
																<button
																	v-if="hasExerciseVideo(ex)"
																	type="button"
																	class="shrink-0 p-0.5 text-black hover:opacity-80 rounded transition-opacity"
																	title="Guarda video"
																	aria-label="Guarda video esercizio"
																	@click.stop="openExerciseVideoModal(ex)"
																>
																	<span class="material-symbols-outlined text-base">videocam</span>
																</button>
															</span>
															<span class="text-gray-600 shrink-0">
																<template v-if="getExerciseWeekSet(ex, week.id).sets != null || getExerciseWeekSet(ex, week.id).repetitions">
																	{{ getExerciseWeekSet(ex, week.id).sets ?? '—' }} × {{ getExerciseWeekSet(ex, week.id).repetitions ?? '—' }}
																</template>
																<template v-else>
																	—
																</template>
															</span>
														</li>
													</ul>
												</div>
											</template>
										</div>
									</div>
								</div>
								<p
									v-if="!cycle.weeks?.length"
									class="px-4 py-3 text-sm text-gray-500"
								>
									Nessuna settimana in questo microciclo.
								</p>
							</div>
							<p
								v-if="!selectedTrainingPlanDetail.cycles?.length"
								class="text-sm text-gray-500"
							>
								Nessun microciclo in questo piano.
							</p>
						</div>
					</div>
				</div>
			</div>
		</Teleport>

		<!-- Modale video esercizio -->
		<Teleport to="body">
			<div
				v-if="exerciseVideoModal"
				class="fixed inset-0 z-[60] flex items-center justify-center p-4"
				aria-modal="true"
				role="dialog"
				aria-label="Video esercizio"
			>
				<button
					type="button"
					class="absolute inset-0 bg-black/60 transition-opacity"
					aria-label="Chiudi video"
					@click="closeExerciseVideoModal"
				/>
				<div
					class="relative w-full max-w-2xl bg-white rounded-none shadow-xl flex flex-col max-h-[90vh] overflow-hidden"
					@click.stop
				>
					<div class="flex items-center justify-between shrink-0 px-4 py-3 border-b border-gray-200">
						<h3 class="text-lg font-semibold text-gray-900 truncate pr-2">
							{{ exerciseVideoModal.exerciseName }}
						</h3>
						<button
							type="button"
							class="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors shrink-0"
							aria-label="Chiudi video"
							@click="closeExerciseVideoModal"
						>
							<span class="material-symbols-outlined">close</span>
						</button>
					</div>
					<div class="relative w-full bg-black shrink-0" style="aspect-ratio: 16/9;">
						<template v-if="exerciseVideoModal.video_type === 'youtube' && youtubeEmbedUrl(exerciseVideoModal.video_youtube_url)">
							<iframe
								:src="youtubeEmbedUrl(exerciseVideoModal.video_youtube_url)!"
								title="Video esercizio"
								class="absolute inset-0 w-full h-full"
								referrerpolicy="strict-origin-when-cross-origin"
								allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
								allowfullscreen
							/>
						</template>
						<template v-else-if="exerciseVideoModal.video_type === 'file' && exerciseVideoFileUrl(exerciseVideoModal.video_file_id)">
							<video
								:src="exerciseVideoFileUrl(exerciseVideoModal.video_file_id)!"
								controls
								class="absolute inset-0 w-full h-full object-contain bg-black"
								playsinline
								preload="metadata"
							>
								Il browser non supporta la riproduzione video.
							</video>
						</template>
					</div>
					<a
						v-if="exerciseVideoModal.video_type === 'youtube' && youtubeWatchUrl(exerciseVideoModal.video_youtube_url)"
						:href="youtubeWatchUrl(exerciseVideoModal.video_youtube_url)!"
						target="_blank"
						rel="noopener noreferrer"
						class="inline-flex items-center gap-1.5 px-4 py-3 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition-colors border-t border-gray-100"
					>
						<span class="material-symbols-outlined text-lg">open_in_new</span>
						Guarda su YouTube
					</a>
				</div>
			</div>
		</Teleport>
	</div>
</template>

<style scoped>
:deep(#medical-report-date::-webkit-calendar-picker-indicator) {
	position: absolute;
	right: 0.75rem;
}
</style>
